import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Building2,
  FolderOpen,
  Package,
  Radio,
  MessageSquare,
  History,
  Loader2,
  CalendarIcon,
  Printer,
  ClipboardList,
  PackageCheck,
  Check,
  ChevronsUpDown,
  ExternalLink,
  Trash2,
  Upload,
  X,
  RefreshCw,
  Plus,
  Pencil,
  Truck,
  Undo2,
} from 'lucide-react';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { FixedSizeDialog } from '@/shared/components/FixedSizeDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle as AlertDialogTitleComponent,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useCustomerOrganisations,
  useOrganisationUsers,
  useContractsByCustomer,
  useAccountSites,
  useTicketsByCustomer,
  usePricelists,
  useSubscriptionsByCustomer,
  useInvoicesByQuoteNumber,
  useNextQuoteNumber,
  useDistinctProjectNumbers,
} from '../../hooks';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import { resolveAccountManagerSeed } from '../../utils/account-manager-seed';
import type {
  V3Quote,
  CreateQuoteInput,
  CreateQuoteForNewCustomerInput,
  UpdateQuoteInput,
  QuoteStatus,
  QuotePriority,
  DealType,
  Currency,
  Language,
  TaxCategory,
  PaymentTerms,
  User,
  Organisation,
  Pricelist,
  Subscription,
} from '../../types';
import { TAX_CATEGORIES, TAX_CATEGORY_ORDER, normalizeProvince, provinceToTaxCategory } from '@atek/ca-tax';
import type { V4QuoteFormDetail } from '@/features/accounts/types';
import { QuoteSkuEditor } from '../sku/QuoteSkuEditor';
import { QuoteSensorsTable } from '../sensors/QuoteSensorsTable';
import { SensorSelectDialog } from '../sensors/SensorSelectDialog';
import { QuoteNotes } from '../notes/QuoteNotes';
import { QuoteChangelog } from '../changelog/QuoteChangelog';
import { MiniSiteFormDialog } from '../mini-forms/MiniSiteFormDialog';
import { SiteCombobox } from './SiteCombobox';
import { MiniUserFormDialog } from '../mini-forms/MiniUserFormDialog';
import { AddExistingContractualManagerDialog } from '../mini-forms/AddExistingContractualManagerDialog';
import { CreateShippingLabelModal } from '@/features/planete-courrier';
import { CreateRmaFromQuoteModal } from '@/features/rma';
import { getQuoteHtmlUrlV4, getWorkOrderUrlV4, getPackingSlipUrlV4, getSubscriptionDetail, addSensorToQuote } from '../../api';
import { getAccountMembersForPicker } from '@/features/accounts/api';
import { createInvoice } from '@/features/invoices/api';
import { calculateInvoicedQuantities, calculateLineTotal } from '../../utils/skuCalculations';
import { mergeContractualManagerOptions, CONTRACTUAL_MANAGER_TAGS, getUserDisplayName } from '../../utils/contractual-managers';
import { accountLabelMatchesSearch } from '../../utils/account-search';
import { collectQuoteSubmitFields } from '../../utils/quote-submit-fields';
import { parseHubspotDealId } from '../../utils/hubspot-deal-link';
import { useUpdateQuote, useUploadPoFile, useDeletePoFile, useQuoteSensors, useQuoteTransmitters, useAddSensorToQuote, useRemoveSensorFromQuote, useUpdateHubspotDeal, quoteKeys } from '../../hooks';
import { useHasAnyRole, useHasPermission, useSession } from '@/hooks/useSession';
import { ACCOUNT_MANAGER_ROLES } from '@/shared/constants/roles';
import type { V3QuoteSku, V3QuoteSubtotal, QuoteSensor } from '../../types';

// =============================================================================
// Option constants
// =============================================================================

const STATUS_OPTIONS: { value: QuoteStatus; label: string }[] = [
  { value: 'preparation', label: 'Preparation' },
  { value: 'sent', label: 'Sent' },
  { value: 'waiting-po', label: 'Waiting PO' },
  { value: 'approved', label: 'Approved' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'invoice-ready', label: 'Invoice Ready' },
  { value: 'partially-invoiced', label: 'Partial Invoice' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

const PRIORITY_OPTIONS: { value: QuotePriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const DEALTYPE_OPTIONS: { value: DealType; label: string }[] = [
  { value: 'newbusiness', label: 'New Business' },
  { value: 'existingbusiness', label: 'Existing Business' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'rfp', label: 'RFP' },
];

const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
  { value: 'CAD', label: 'CAD' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
];

const TAX_OPTIONS: { value: TaxCategory; label: string }[] = TAX_CATEGORY_ORDER.map(
  (value) => ({ value, label: TAX_CATEGORIES[value].label }),
);

const PAYMENT_OPTIONS: { value: PaymentTerms; label: string }[] = [
  { value: 'on-order', label: 'On Order' },
  { value: 'net-0', label: 'Net 0' },
  { value: 'net-30', label: 'Net 30' },
  { value: 'net-45', label: 'Net 45' },
  { value: 'net-60', label: 'Net 60' },
];

// Fields that are always editable regardless of status
const ALWAYS_EDITABLE = new Set([
  'status', 'priority', 'sales_manager', 'po_number', 'next_step', 'next_step_deadline',
  'tracking_numbers', 'hubspot_link', 'gdrive_link', 'internal_notes', 'comment',
]);

const PG_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MONGO_OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

function isPgUuid(value?: string) {
  return !!value && PG_UUID_PATTERN.test(value);
}

/**
 * Compose the one-line address stored on the quote from a site's V4 fields.
 * V1 parity (quote-form/component.js): province + postal code are part of the
 * line, and the country only appears when it is not Canada.
 */
function composeSiteAddress(site: {
  addressLine1?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
}): string {
  return [
    site.addressLine1,
    site.city,
    site.stateProvince,
    site.postalCode,
    site.country && site.country !== 'Canada' ? site.country : undefined,
  ].filter(Boolean).join(', ');
}

function QuoteFormSection({
  title,
  icon,
  accent,
  children,
  className,
}: {
  title: string;
  icon: ReactNode;
  accent: 'blue' | 'green' | 'amber' | 'indigo' | 'teal' | 'slate' | 'purple';
  children: ReactNode;
  className?: string;
}) {
  const accentClasses = {
    blue: 'border-sky-200/80 [&>div:first-child]:border-sky-200/80 [&>div:first-child]:bg-sky-50/60',
    green: 'border-emerald-200/80 [&>div:first-child]:border-emerald-200/80 [&>div:first-child]:bg-emerald-50/60',
    amber: 'border-amber-200/80 [&>div:first-child]:border-amber-200/80 [&>div:first-child]:bg-amber-50/60',
    indigo: 'border-indigo-200/80 [&>div:first-child]:border-indigo-200/80 [&>div:first-child]:bg-indigo-50/60',
    teal: 'border-teal-200/80 [&>div:first-child]:border-teal-200/80 [&>div:first-child]:bg-teal-50/60',
    slate: 'border-slate-200/80 [&>div:first-child]:border-slate-200/80 [&>div:first-child]:bg-slate-50/70',
    purple: 'border-violet-200/80 [&>div:first-child]:border-violet-200/80 [&>div:first-child]:bg-violet-50/60',
  };

  return (
    <section className={cn('rounded-md border bg-background', accentClasses[accent], className)}>
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="space-y-4 p-4">
        {children}
      </div>
    </section>
  );
}

// =============================================================================
// Form data interface
// =============================================================================

interface FormData {
  // Details
  status: QuoteStatus;
  priority: QuotePriority;
  dealtype: DealType;
  probability: number;
  date: string;
  expiration_date: string;
  estimated_delivery: string;
  payment_terms: PaymentTerms;
  currency: Currency;
  language: Language;
  tax_category: TaxCategory;
  customer: string;
  contract: string;
  ticket: string;
  subscription: string;
  pricelist: string;
  quote_number: string;
  quote_version: number;
  sales_manager: string;
  contractual_manager: string;
  billing_site: string;
  billing_address: string;
  shipping_site: string;
  shipping_address: string;
  // Project
  project_name: string;
  project_number: string;
  description: string;
  comment: string;
  internal_notes: string;
  po_number: string;
  next_step: string;
  next_step_deadline: string;
  hubspot_link: string;
  gdrive_link: string;
  tracking_numbers: string[];
  // SKUs
  skus: V3QuoteSku[];
  subtotals: V3QuoteSubtotal[];
}

const DEFAULT_FORM: FormData = {
  status: 'preparation',
  priority: 'normal',
  dealtype: 'newbusiness',
  probability: 0,
  date: '',
  expiration_date: '',
  estimated_delivery: '',
  payment_terms: 'net-30',
  currency: 'CAD',
  language: 'en',
  tax_category: 'GST+QST',
  customer: '',
  contract: '',
  ticket: '',
  subscription: '',
  pricelist: '',
  quote_number: '',
  quote_version: 1,
  sales_manager: '',
  contractual_manager: '',
  billing_site: '',
  billing_address: '',
  shipping_site: '',
  shipping_address: '',
  project_name: '',
  project_number: '',
  description: '',
  comment: '',
  internal_notes: '',
  po_number: '',
  next_step: '',
  next_step_deadline: '',
  hubspot_link: '',
  gdrive_link: '',
  tracking_numbers: [],
  skus: [],
  subtotals: [],
};

// =============================================================================
// Helper
// =============================================================================

function formatDateForInput(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

function getDefaultQuoteDates() {
  const today = new Date();
  return {
    date: format(today, 'yyyy-MM-dd'),
    expiration_date: format(addDays(today, 30), 'yyyy-MM-dd'),
  };
}

function createDefaultQuoteForm(quoteNumber = ''): FormData {
  return {
    ...DEFAULT_FORM,
    ...getDefaultQuoteDates(),
    quote_number: quoteNumber,
  };
}

async function printHtmlDocument(html: string, fileName: string) {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';
  document.body.appendChild(frame);

  const previousTitle = document.title;
  document.title = fileName;

  const frameDocument = frame.contentDocument;
  if (!frameDocument || !frame.contentWindow) {
    document.title = previousTitle;
    frame.remove();
    throw new Error('Print frame could not be created');
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  await new Promise((resolve) => window.setTimeout(resolve, 1000));
  frame.contentWindow.focus();
  frame.contentWindow.print();
  window.focus();
  document.title = previousTitle;
  frame.remove();
}

function cloneFormData(data: FormData): FormData {
  return {
    ...data,
    tracking_numbers: [...data.tracking_numbers],
    skus: data.skus.map((sku) => ({ ...sku })),
    subtotals: data.subtotals.map((subtotal) => ({ ...subtotal })),
  };
}

function roundAmount(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

function getSkuLineTotal(sku: V3QuoteSku): number {
  if (sku.sku === '__note__' || sku.sku === '__section__') return 0;
  return calculateLineTotal(
    Number(sku.quantity) || 0,
    Number(sku.unit_price) || 0,
    Number(sku.discount) || 0,
  );
}

function buildUpdatedSkus(
  skus: V3QuoteSku[],
  options: { stripInvalidMongoIds?: boolean } = {},
): V3QuoteSku[] {
  return skus.map((sku) => {
    const updatedSku = {
      ...sku,
      total: roundAmount(getSkuLineTotal(sku)),
    };

    if (options.stripInvalidMongoIds && updatedSku._id && !MONGO_OBJECT_ID_PATTERN.test(updatedSku._id)) {
      delete updatedSku._id;
    }

    return updatedSku;
  });
}

function buildUpdatedSubtotals(
  skus: V3QuoteSku[],
  existingSubtotals: V3QuoteSubtotal[] = [],
): V3QuoteSubtotal[] {
  return existingSubtotals.map((subtotal, index) => {
    const subtotalId = subtotal._id || subtotal.displayName?.toLowerCase().replace(/\s+/g, '-') || `subtotal-${index + 1}`;
    const total = skus.reduce(
      (sum, sku) => (sku.subtotal_id === subtotalId ? sum + (sku.total || 0) : sum),
      0,
    );

    return {
      ...subtotal,
      _id: subtotalId,
      total: roundAmount(total),
    };
  });
}

function calculateQuoteTotal(skus: V3QuoteSku[]): number {
  return roundAmount(skus.reduce((sum, sku) => sum + (sku.total || 0), 0));
}

// #883 — quotes were silently saved with a $0 total because the discount
// column read as a bare "%" and users keyed 100 into it. A save whose priced
// lines (qty > 0 AND unit price > 0) net to $0 needs an explicit
// confirmation: warranty/replacement/demo quotes are legitimately $0, so
// this is a confirm, not a block. Free lines (unit price 0) never trigger it.
function needsZeroTotalConfirmation(data: Record<string, unknown>): boolean {
  const skus = (data.skus as V3QuoteSku[] | undefined) ?? [];
  const hasPricedLines = skus.some(
    (sku) => sku.sku !== '__note__' && sku.sku !== '__section__'
      && Number(sku.quantity) > 0 && Number(sku.unit_price) > 0,
  );
  return hasPricedLines && Math.round((Number(data.total) || 0) * 100) === 0;
}

function filterInvoiceableSkus(skus: V3QuoteSku[]): V3QuoteSku[] {
  return skus.filter((sku) => sku.sku !== '__note__' && sku.sku !== '__section__');
}

function buildSubtotalsFromLineItems(lineItems: V4QuoteFormDetail['lineItems'] = []): V3QuoteSubtotal[] {
  const subtotalsMap = new Map<string, V3QuoteSubtotal>();

  for (const lineItem of lineItems) {
    if (!lineItem.subtotalGroup) continue;

    const existing = subtotalsMap.get(lineItem.subtotalGroup);
    const lineTotal = Number.parseFloat(lineItem.total) || 0;

    if (existing) {
      existing.total = roundAmount((existing.total || 0) + lineTotal);
      continue;
    }

    subtotalsMap.set(lineItem.subtotalGroup, {
      _id: lineItem.subtotalGroup,
      displayName: lineItem.subtotalLabel || lineItem.subtotalGroup,
      total: roundAmount(lineTotal),
    });
  }

  return Array.from(subtotalsMap.values());
}

// Map a V4 quote detail (PostgreSQL) into the modal's flat FormData shape.
// Shared by the edit fallback (used when the V3 doc hasn't loaded yet) and the
// duplicate flow (which seeds a brand-new quote from an existing one). The
// `customer` value differs per caller, so it is passed in rather than derived.
function mapV4DetailToFormData(v4Quote: V4QuoteFormDetail, customer: string): FormData {
  return {
    status: (v4Quote.status || 'preparation') as QuoteStatus,
    priority: v4Quote.priority || 'normal',
    dealtype: (v4Quote.dealType || 'newbusiness') as DealType,
    probability: v4Quote.probability || 0,
    date: v4Quote.quoteDate || '',
    expiration_date: v4Quote.expirationDate || '',
    estimated_delivery: v4Quote.estimatedDelivery || '',
    payment_terms: (v4Quote.paymentTerms || 'net-30') as PaymentTerms,
    currency: (v4Quote.currency || 'CAD') as Currency,
    language: (v4Quote.language || 'en') as Language,
    tax_category: (v4Quote.taxCategory || 'GST+QST') as TaxCategory,
    customer,
    contract: v4Quote.subscriptionId || '',
    ticket: v4Quote.ticket || '',
    subscription: v4Quote.subscriptionId || '',
    pricelist: v4Quote.pricelist || '',
    quote_number: v4Quote.quoteNumber || '',
    quote_version: v4Quote.quoteVersion || 1,
    sales_manager: v4Quote.salesManagerId || '',
    contractual_manager: v4Quote.contractualManagerId || '',
    billing_site: v4Quote.billingSite || '',
    shipping_site: v4Quote.shippingSite || '',
    billing_address: v4Quote.billingAddress || '',
    shipping_address: v4Quote.shippingAddress || '',
    project_name: v4Quote.projectName || '',
    project_number: v4Quote.projectNumber || '',
    description: v4Quote.description || '',
    comment: v4Quote.comment || '',
    internal_notes: v4Quote.internalNotes || '',
    po_number: v4Quote.poNumber || '',
    next_step: v4Quote.nextStep || '',
    next_step_deadline: v4Quote.nextStepDeadline || '',
    hubspot_link: v4Quote.hubspotLink || '',
    gdrive_link: v4Quote.gdriveLink || '',
    tracking_numbers: v4Quote.trackingNumbers || [],
    skus: (v4Quote.lineItems || []).map((li) => ({
      _id: li.id,
      sku: li.sku || '',
      name: li.name || '',
      description: li.description || '',
      quantity: parseFloat(li.quantity) || 0,
      unit_price: parseFloat(li.unitPrice) || 0,
      discount: parseFloat(li.discount) || 0,
      item_number: li.itemNumber ?? undefined,
      subtotal_id: li.subtotalGroup || undefined,
    })),
    subtotals: buildSubtotalsFromLineItems(v4Quote.lineItems || []),
  };
}

// =============================================================================
// DatePickerField subcomponent
// =============================================================================

function DatePickerField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  const date = value ? parseISO(value) : undefined;

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn('w-full justify-start text-left font-normal', !date && 'text-muted-foreground')}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, 'PPP') : label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// =============================================================================
// QuoteFormModal
// =============================================================================

interface QuoteFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote?: V3Quote | null;
  v4Quote?: V4QuoteFormDetail | null;
  /** When set (and no quote/v4Quote), seed a brand-new quote from this existing
   *  quote's content — the Duplicate action. The form stays in create mode. */
  duplicateFrom?: V4QuoteFormDetail | null;
  onSubmit: (data: CreateQuoteInput | UpdateQuoteInput) => Promise<V3Quote | void>;
  /** When set, surfaces a "New customer" toggle on create — a single
   *  customer name field, no account picker. Routes the submit to the
   *  PG-native new-customer create instead of `onSubmit`. */
  onCreateForNewCustomer?: (input: CreateQuoteForNewCustomerInput) => Promise<void>;
  isLoading?: boolean;
  pgQuoteId?: string;
  ensuredMongoQuoteId?: string;
  initialCustomerData?: {
    customerId?: string;
    accountName?: string;
    accountNumber?: string;
    salesManagerId?: string;
    contractualManagerId?: string;
    contractId?: string;
    pgAccountId?: string;
    lockCustomer?: boolean;
  };
  invoiceWorkflow?: boolean;
  /** Called after an invoice is successfully created; `fullyInvoiced` is true
   *  when the quote became fully (not partially) invoiced. Lets the caller
   *  advance the operations project out of the Invoicing stage. */
  onInvoiceCreated?: (fullyInvoiced: boolean) => void;
}

export function QuoteFormModal({
  open,
  onOpenChange,
  quote,
  v4Quote,
  duplicateFrom,
  onSubmit,
  onCreateForNewCustomer,
  isLoading,
  pgQuoteId,
  ensuredMongoQuoteId,
  initialCustomerData,
  invoiceWorkflow = false,
  onInvoiceCreated,
}: QuoteFormModalProps) {
  const { t } = useTranslation('quotes');
  const queryClient = useQueryClient();
  const isEditing = !!quote || !!v4Quote;
  // Derive MongoDB quote ID from whichever source is available (used for V3 mutations)
  const mongoQuoteId = quote?._id || v4Quote?.mongoQuoteId || ensuredMongoQuoteId || undefined;
  // Prefer the PG UUID for updates so V4 read paths (Kanban, Print, lists)
  // reflect the change immediately. updateQuote() routes UUID → PG, falling
  // back to the Mongo id for quotes not yet materialized in PostgreSQL.
  const statusUpdateId = pgQuoteId || v4Quote?.id || mongoQuoteId;
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
  const latestFormDataRef = useRef(formData);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [contractualManagerOpen, setContractualManagerOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [projectNumberOpen, setProjectNumberOpen] = useState(false);
  const [projectNumberSearch, setProjectNumberSearch] = useState('');
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  // New-customer create is a one-shot action: the backend dedups the ACCOUNT
  // by name but not the quote, so a second Save (the keep-open button, or a
  // fast double-click before the modal closes) would mint a duplicate
  // header-only quote. Guards saveQuoteForm's new-customer branch below.
  const newCustomerSubmittedRef = useRef(false);

  // Status workflow mutation (separate from form submit)
  const statusUpdateMutation = useUpdateQuote();
  const uploadPoFileMutation = useUploadPoFile();
  const deletePoFileMutation = useDeletePoFile();
  // Sync only: the deal is created by a human in HubSpot (ATE-11441), so the id
  // comes from the link the quote already carries.
  const hubspotDealSync = useUpdateHubspotDeal();
  const hubspotDealId = parseHubspotDealId(formData.hubspot_link);
  const poFileInputRef = useRef<HTMLInputElement>(null);
  const closeAfterSaveRef = useRef(true);

  // Queries for dropdowns
  const { data: salesManagers = [] } = useSalesManagers();
  const { user: currentUser } = useSession();
  const currentUserAccountManagerValue = useMemo(() => {
    const roles = currentUser?.postgres_roles || [];
    if (!ACCOUNT_MANAGER_ROLES.some((role) => roles.includes(role))) {
      return '';
    }

    const currentUserIds = [currentUser?.pg_id, currentUser?._id].filter(Boolean);
    const match = salesManagers.find((manager) => (
      currentUserIds.includes(manager.id) || (manager.mongoUserId ? currentUserIds.includes(manager.mongoUserId) : false)
    ));
    return match ? (match.mongoUserId || match.id) : '';
  }, [currentUser?._id, currentUser?.pg_id, currentUser?.postgres_roles, salesManagers]);
  const resolveAccountManagerFormValue = useCallback((accountSalesManagerId?: string | null) => (
    resolveAccountManagerSeed(accountSalesManagerId, salesManagers, currentUserAccountManagerValue)
  ), [currentUserAccountManagerValue, salesManagers]);
  const { data: customers = [] } = useCustomerOrganisations();
  const customerLookupId = formData.customer || initialCustomerData?.pgAccountId || initialCustomerData?.customerId || '';
  const selectedCustomer = useMemo(
    () => customers.find((c) => c._id === customerLookupId || c.mongoOrgId === customerLookupId),
    [customers, customerLookupId]
  );
  const customerPgAccountId = selectedCustomer?._id || initialCustomerData?.pgAccountId;
  const customerMongoOrgId = selectedCustomer?.mongoOrgId
    || (formData.customer && !isPgUuid(formData.customer) ? formData.customer : undefined)
    || (initialCustomerData?.customerId && !isPgUuid(initialCustomerData.customerId) ? initialCustomerData.customerId : undefined);

  // READ-ONLY fallback for the org-scoped pickers (sites, contacts, tickets,
  // subscriptions). A sub-account (`0013-07`) can never own a Mongo organisation —
  // accounts_mongo_organisation_id_key is UNIQUE — so scoping those lookups to its
  // own org leaves every picker empty. The resources live on the parent: account
  // 0013 holds 19 sites, its sub-accounts hold almost none, and one of those 19 IS
  // the branch's address.
  //
  // Deliberately NOT used for writing. `data.customer` below still uses
  // customerMongoOrgId, so a sub-account quote is never stored against the parent's
  // organisation — that is the mis-attribution this whole change exists to stop.
  // Reads borrow from the family; writes stay on the account that was picked.
  const parentMongoOrgId = useMemo(() => {
    const num = selectedCustomer?.accountNumber;
    if (!num || !num.includes('-')) return undefined;
    const parentNumber = num.split('-')[0];
    return customers.find((o: Organisation) => o.accountNumber === parentNumber)?.mongoOrgId;
  }, [selectedCustomer, customers]);
  const pickerOrgId = customerMongoOrgId || parentMongoOrgId;

  // Cascading queries based on selected customer
  const { data: contracts = [] } = useContractsByCustomer(pickerOrgId || undefined, customerPgAccountId);
  // Sites come from the V4 sites API keyed by PG accountId, so the family rule
  // above applies on the PG side: the parent account (0013) holds the sites,
  // a sub-account (0013-07) holds almost none — read the parent's when one exists.
  const parentPgAccountId = useMemo(() => {
    const num = selectedCustomer?.accountNumber;
    if (!num || !num.includes('-')) return undefined;
    const parentNumber = num.split('-')[0];
    return customers.find((o: Organisation) => o.accountNumber === parentNumber)?._id;
  }, [selectedCustomer, customers]);
  const sitesPgAccountId = parentPgAccountId || customerPgAccountId;
  const { data: allSites = [] } = useAccountSites(sitesPgAccountId || undefined);
  const {
    data: tickets = [],
    isLoading: ticketsLoading,
    isError: ticketsError,
  } = useTicketsByCustomer(pickerOrgId || undefined);
  // Prefer V4 endpoint (PostgreSQL usergroup-members) when an account UUID is available,
  // but fall back to V3 contacts when the account has no linked members yet.
  const { data: v4Members } = useQuery({
    queryKey: ['account-members-picker', customerPgAccountId],
    queryFn: () => getAccountMembersForPicker(customerPgAccountId!),
    enabled: !!customerPgAccountId,
    staleTime: 5 * 60 * 1000,
  });
  const { data: v3OrgUsers } = useOrganisationUsers(pickerOrgId || undefined, CONTRACTUAL_MANAGER_TAGS);
  // Merge the V4 account members tagged "Contractual Manager" with the V3 tagged contacts
  // (deduped, sorted). See utils/contractual-managers.ts — this previously short-circuited to
  // V4-only when the account had members, dropping the V3 contacts so the field looked empty.
  // Contacts tagged from the picker during this session. Merged into the options
  // so the trigger shows the pick immediately, before the tagged query refetches.
  const [addedManagers, setAddedManagers] = useState<User[]>([]);
  const orgUsers: User[] = useMemo(
    () => mergeContractualManagerOptions(
      v4Members,
      [...(v3OrgUsers ?? []), ...addedManagers],
      formData.contractual_manager,
    ),
    [v4Members, v3OrgUsers, addedManagers, formData.contractual_manager],
  );
  const taggedManagerIds = useMemo(() => new Set(orgUsers.map((u) => u._id)), [orgUsers]);
  const { data: pricelists = [] } = usePricelists();
  const { data: allSubscriptions = [] } = useSubscriptionsByCustomer(pickerOrgId || undefined);
  const subscriptions = useMemo(
    () => pickerOrgId ? allSubscriptions.filter((s: Subscription) => s.customer === pickerOrgId) : allSubscriptions,
    [allSubscriptions, pickerOrgId]
  );
  const { data: nextQuoteNumber } = useNextQuoteNumber(!isEditing && open);
  const { data: projectNumbers = [] } = useDistinctProjectNumbers();
  const quoteNumber = isEditing ? (v4Quote?.quoteNumber || quote?.quote_number) : undefined;
  const { data: linkedInvoices = [] } = useInvoicesByQuoteNumber(quoteNumber);

  const initialCustomerDisplay = useMemo(() => {
    const accountName = initialCustomerData?.accountName?.trim();
    const accountNumber = initialCustomerData?.accountNumber?.trim();
    if (accountName && accountNumber) return `${accountNumber} ${accountName}`;
    return accountName || accountNumber || '';
  }, [initialCustomerData?.accountName, initialCustomerData?.accountNumber]);
  const isCustomerLocked = Boolean(
    initialCustomerData?.lockCustomer
    && (initialCustomerData?.customerId || initialCustomerData?.pgAccountId || formData.customer)
  );

  // Find selected contractual manager for display
  const selectedContractualManager = useMemo(
    () => orgUsers.find((u) => u._id === formData.contractual_manager),
    [orgUsers, formData.contractual_manager]
  );
  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket._id === formData.ticket),
    [tickets, formData.ticket]
  );

  // Selected billing site for email display
  const selectedBillingSite = useMemo(
    () => allSites.find((s) => s.mongoSiteId === formData.billing_site),
    [allSites, formData.billing_site]
  );

  // Place-of-supply check: warn when the chosen tax doesn't match the billing
  // destination. DND (account 0063) is GST-only regardless of province.
  const expectedTaxCategory = useMemo<TaxCategory | null>(() => {
    const acctDigits = (initialCustomerData?.accountNumber || '').replace(/\D/g, '');
    if (acctDigits === '0063') return 'GST';
    // `state_province_county` is free text in the legacy store, so a literal
    // two-letter comparison silently ignored most of it: of 81 populated sites
    // measured in production, 57 read "QC" but 20 read "Québec", plus "QUÉBEC"
    // and "QC - Québec". That is ~30% of billing destinations for which this
    // warning could never fire. normalizeProvince folds case, diacritics and
    // separators, and returns null for anything genuinely unrecognizable — so an
    // unparseable value still keeps the warning silent rather than guessing.
    const prov = normalizeProvince(selectedBillingSite?.stateProvince);
    if (!prov) return null;
    return provinceToTaxCategory(prov);
  }, [initialCustomerData?.accountNumber, selectedBillingSite?.stateProvince]);
  const taxMismatch =
    expectedTaxCategory != null &&
    !!formData.tax_category &&
    expectedTaxCategory !== formData.tax_category;

  // Calculate invoiced quantities for SKU editor
  const invoicedQuantities = useMemo(
    () => calculateInvoicedQuantities(formData.skus, linkedInvoices),
    [formData.skus, linkedInvoices]
  );

  // Sensors section
  const canReadSensors = useHasPermission('sensors.read');
  const canCreateInvoice = useHasPermission('invoices.create');
  // Slice-1 scope for the new-customer create is header-only: there is no
  // account yet for sensors to be linked to, so staging must be impossible —
  // not just discouraged — or picks get silently dropped on submit (the
  // new-customer submit branch never looks at stagedSensors).
  const sensorsDisabledForNewCustomer = !isEditing && newCustomerMode;
  const [sensorsToInvoice, setSensorsToInvoice] = useState<string[]>([]);
  const invoiceSensorPrefillAppliedRef = useRef(false);
  const [sensorSelectOpen, setSensorSelectOpen] = useState(false);
  // Sensors picked while CREATING a quote (no quote_number yet). They live in
  // local state until the quote is saved, then get attached via add-quote.
  const [stagedSensors, setStagedSensors] = useState<QuoteSensor[]>([]);
  // The server-assigned quote_number captured right after a create, so the
  // sensor section can switch from "staged" to live add/remove without the
  // modal having to be reopened in edit mode (handles the keep-open "Save").
  const [createdQuoteNumber, setCreatedQuoteNumber] = useState<string | undefined>(undefined);
  // Effective quote number for sensor operations: an existing quote's number
  // when editing, the just-created number after a create, else undefined.
  const sensorsQuoteNumber = quoteNumber ?? createdQuoteNumber;
  const { sensors: quoteSensors, isLoading: sensorsLoading, refetch: refetchSensors } =
    useQuoteSensors(sensorsQuoteNumber, quote?.invoice_numbers);
  const { data: quoteTransmitters } = useQuoteTransmitters(quoteNumber);
  const addSensorMutation = useAddSensorToQuote();
  const removeSensorMutation = useRemoveSensorFromQuote();
  // Before the quote exists, show the staged picks; once it has a number, show
  // the persisted (live) sensors.
  const sensorsForDisplay = sensorsQuoteNumber ? quoteSensors : stagedSensors;
  const quoteSensorIds = useMemo(() => new Set(sensorsForDisplay.map(s => s._id)), [sensorsForDisplay]);

  // Mini-form dialog state
  const [miniSiteOpen, setMiniSiteOpen] = useState(false);
  const [miniSiteTag, setMiniSiteTag] = useState<string>('billing');
  const [editingSiteData, setEditingSiteData] = useState<Record<string, unknown> | null>(null);
  const [miniUserOpen, setMiniUserOpen] = useState(false);
  const [addExistingManagerOpen, setAddExistingManagerOpen] = useState(false);
  const [shippingLabelOpen, setShippingLabelOpen] = useState(false);
  const [rmaFromQuoteOpen, setRmaFromQuoteOpen] = useState(false);
  const canCreateShippingLabel = useHasAnyRole(['full_admin', 'director', 'operations_manager']);
  const prefillAppliedRef = useRef(false);
  const createFormInitializedRef = useRef(false);
  // Guards the existing-quote seed so it runs ONCE per opened quote. Any later
  // refetch of the detail query (e.g. the invalidateQueries in
  // useUpdateQuote.onSuccess fired by a keep-open Save) hands the seed effect a
  // NEW quote object with the same data; without this guard setFormData() re-ran
  // and overwrote the user's unsaved edits — every field, not just line items.
  // Keyed on quote identity + source so a legitimately different quote re-seeds,
  // and a late V3 (Mongo) load may upgrade a stale V4 (PG) seed exactly once
  // (the V3-first-during-CDC-lag behavior from PR #500). Reset on close (below).
  const seededQuoteKeyRef = useRef<string | null>(null);
  const seededQuoteSourceRef = useRef<'v3' | 'v4' | null>(null);

  // Quote number duplicate check
  // quoteNumberStatus is used in validateQuoteForm (edit-mode duplicate guard).
  // On create the field is disabled so this stays 'idle' and the check never fires.
  const [quoteNumberStatus, setQuoteNumberStatus] = useState<'idle' | 'checking' | 'duplicate' | 'available'>('idle');

  // Reset status when quote number changes (user is typing)
  useEffect(() => {
    if (quoteNumberStatus !== 'idle' && quoteNumberStatus !== 'checking') {
      setQuoteNumberStatus('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.quote_number]);

  const handleSiteSaved = (siteId: string, siteData: Record<string, unknown>) => {
    const addr = [siteData.name, composeSiteAddress(siteData as Parameters<typeof composeSiteAddress>[0])]
      .filter(Boolean).join(', ');
    if (miniSiteTag === 'billing') {
      updateField('billing_site', siteId);
      updateField('billing_address', addr);
    } else {
      updateField('shipping_site', siteId);
      updateField('shipping_address', addr);
    }
    // Invalidate sites query to refresh dropdowns
    if (customerPgAccountId) {
      queryClient.invalidateQueries({ queryKey: [...quoteKeys.all, 'sites', customerPgAccountId] });
    }
  };

  const handleUserSaved = (userId: string) => {
    updateField('contractual_manager', userId);
    // Invalidate org users query to refresh dropdown
    if (customerPgAccountId) {
      queryClient.invalidateQueries({ queryKey: ['account-members-picker', customerPgAccountId] });
    }
    if (customerMongoOrgId) {
      queryClient.invalidateQueries({ queryKey: quoteKeys.orgUsers(customerMongoOrgId) });
    }
  };

  const handleExistingManagerAdded = (user: User) => {
    setAddedManagers((prev) => (prev.some((u) => u._id === user._id) ? prev : [...prev, user]));
    handleUserSaved(user._id);
  };

  function getOrgDisplayName(org: Organisation) {
    // Prefer the PostgreSQL account number: it is the only field that carries
    // the `-NN` sub-account suffix, and many accounts share a name (9 are called
    // exactly "Acces Pharma"), so the number is what tells them apart. Fall back
    // to the padded legacy organisation number for V3-sourced callers.
    const num = org.accountNumber?.trim()
      || (org.org_num != null ? String(org.org_num).padStart(4, '0') : '');
    return num ? `${num} ${org.name}` : org.name;
  }

  const normalize = (value?: string) => (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const accountNumberDigits = (initialCustomerData?.accountNumber || '').replace(/\D/g, '');

  const matchedCustomerId = useMemo(() => {
    if (!initialCustomerData || customers.length === 0) return '';

    const byNumber = accountNumberDigits
      ? customers.find((org) => {
          if (org.org_num == null) return false;
          const orgDigits = String(org.org_num).replace(/\D/g, '');
          return orgDigits === accountNumberDigits || String(org.org_num) === String(Number(accountNumberDigits));
        })
      : undefined;

    if (byNumber?._id) return byNumber._id;

    const normalizedAccountName = normalize(initialCustomerData.accountName);
    if (!normalizedAccountName) return '';

    const byExactName = customers.find((org) => normalize(org.name) === normalizedAccountName);
    if (byExactName?._id) return byExactName._id;

    const byFuzzyName = customers.find((org) => {
      const normalizedOrgName = normalize(org.name);
      return normalizedOrgName.includes(normalizedAccountName)
        || normalizedAccountName.includes(normalizedOrgName);
    });
    return byFuzzyName?._id || '';
  }, [customers, initialCustomerData, accountNumberDigits]);

  // Determine editability based on quote status
  const isPreparation = !isEditing || formData.status === 'preparation';
  const isFieldEditable = (fieldName: string) => {
    // Creating a new quote - no restrictions
    if (!isEditing) return true;
    // Status-based editability
    if (ALWAYS_EDITABLE.has(fieldName)) return true;
    return isPreparation;
  };

  useEffect(() => {
    if (!open) {
      prefillAppliedRef.current = false;
      createFormInitializedRef.current = false;
      seededQuoteKeyRef.current = null;
      seededQuoteSourceRef.current = null;
      invoiceSensorPrefillAppliedRef.current = false;
      setQuoteNumberStatus('idle');
      setStagedSensors([]);
      setCreatedQuoteNumber(undefined);
      newCustomerSubmittedRef.current = false;
    }
  }, [open]);

  // Reset the new-customer toggle + fields whenever the modal opens for a
  // fresh create — never for editing, and never mid-session while it's open.
  useEffect(() => {
    if (open && !isEditing) {
      setNewCustomerMode(false);
      setNewCustomerName('');
    }
  }, [open, isEditing]);

  useEffect(() => {
    if (
      !open
      || !invoiceWorkflow
      || invoiceSensorPrefillAppliedRef.current
      || quoteSensors.length === 0
    ) {
      return;
    }

    setSensorsToInvoice(
      quoteSensors
        .filter((sensor) => !sensor.invoices || sensor.invoices.length === 0)
        .map((sensor) => sensor._id)
    );
    invoiceSensorPrefillAppliedRef.current = true;
  }, [open, invoiceWorkflow, quoteSensors]);

  useEffect(() => {
    if (!open || isEditing || !initialCustomerData || prefillAppliedRef.current) {
      return;
    }

    const hasCustomerHint = Boolean(initialCustomerData.accountName || initialCustomerData.accountNumber);
    const hasStableCustomerId = Boolean(initialCustomerData.customerId);
    if (!hasStableCustomerId && hasCustomerHint && customers.length === 0) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      customer: prev.customer || initialCustomerData.customerId || matchedCustomerId || prev.customer,
      contract: prev.contract || initialCustomerData.contractId || prev.contract,
      sales_manager: prev.sales_manager || resolveAccountManagerFormValue(initialCustomerData.salesManagerId),
      contractual_manager: prev.contractual_manager || initialCustomerData.contractualManagerId || prev.contractual_manager,
    }));
    prefillAppliedRef.current = true;
  }, [open, isEditing, initialCustomerData, customers.length, matchedCustomerId, resolveAccountManagerFormValue]);

  // Populate form when editing.
  // Prefer V3 (MongoDB) over V4 (PostgreSQL) so the form reflects the same
  // source we write to — V4 lags behind MongoDB via accounts_sync CDC and would
  // surface stale values immediately after Save and Close. PG-owned Mongo
  // shadows (`migrated_to_pg`) are the exception: V4 is the canonical copy.
  useEffect(() => {
    // Don't seed while the modal is closed. The page keeps `open` false until a
    // pending Edit/Duplicate data fetch resolves; during that window
    // duplicateFrom/quote/v4Quote are all still null, so the create branch below
    // would flip createFormInitializedRef and pre-empt the real seed once the
    // data arrives (a duplicate would otherwise open blank — number only).
    if (!open) return;
    const shouldUseV3Quote = quote && !(quote.migrated_to_pg === true && v4Quote);

    if (shouldUseV3Quote) {
      // Seed once per quote from V3. A refetch of the same quote (same key,
      // already V3-seeded) must not clobber the user's unsaved edits; a genuinely
      // different quote, or an upgrade from an earlier V4 seed, still seeds.
      const editKey = quote.quote_number || quote._id || null;
      if (seededQuoteKeyRef.current === editKey && seededQuoteSourceRef.current === 'v3') {
        return;
      }
      seededQuoteKeyRef.current = editKey;
      seededQuoteSourceRef.current = 'v3';

      const getIdFromPopulated = (
        value: string | { _id: string } | undefined
      ): string => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        return value._id;
      };

      const mongoContractId = getIdFromPopulated(quote.contract);
      const pgContract = mongoContractId
        ? contracts.find(c => c.mongoContractId === mongoContractId)
        : undefined;

      setFormData({
        status: quote.status || 'preparation',
        // Legacy Mongo quotes have no priority — pre-fill the default.
        priority: 'normal',
        dealtype: quote.dealtype || 'newbusiness',
        probability: quote.probability || 0,
        date: formatDateForInput(quote.date),
        expiration_date: formatDateForInput(quote.expiration_date),
        estimated_delivery: formatDateForInput(quote.estimated_delivery),
        payment_terms: quote.payment_terms || 'net-30',
        currency: quote.currency || 'CAD',
        language: quote.language || 'en',
        tax_category: quote.tax_category || 'GST+QST',
        customer: (initialCustomerData?.lockCustomer && initialCustomerData?.customerId)
          ? initialCustomerData.customerId
          : (getIdFromPopulated(quote.customer) || initialCustomerData?.customerId || ''),
        contract: pgContract?._id || mongoContractId,
        ticket: quote.ticket || '',
        subscription: quote.subscription || '',
        pricelist: quote.pricelist || '',
        quote_number: quote.quote_number || '',
        quote_version: quote.quote_version || 1,
        sales_manager: getIdFromPopulated(quote.sales_manager) || initialCustomerData?.salesManagerId || '',
        contractual_manager: getIdFromPopulated(quote.contractual_manager) || initialCustomerData?.contractualManagerId || '',
        // When the V3 shadow predates the address-inclusion fix (a PG-owned quote
        // whose thin/old Mongo shadow was never re-stamped), its address fields are
        // blank while the real values live in PG. Fall back to the already-loaded
        // V4 detail so the addresses still seed the form. Read-side only — the
        // submit path (buildQuoteSubmitData) is untouched, so no stale value is
        // ever written back. V4 site ids are Mongo ObjectIds, compatible with the
        // Mongo-keyed site dropdowns.
        billing_site: getIdFromPopulated(quote.billing_site) || v4Quote?.billingSite || '',
        billing_address: quote.billing_address || v4Quote?.billingAddress || '',
        shipping_site: getIdFromPopulated(quote.shipping_addresses?.[0]?.site) || v4Quote?.shippingSite || '',
        shipping_address: quote.shipping_addresses?.[0]?.address || v4Quote?.shippingAddress || '',
        project_name: quote.project_name || '',
        project_number: quote.project_number || '',
        description: quote.description || '',
        comment: quote.comment || '',
        internal_notes: quote.internal_notes || '',
        po_number: quote.po_number || '',
        next_step: quote.next_step || '',
        next_step_deadline: formatDateForInput(quote.next_step_deadline),
        hubspot_link: quote.hubspot_link || '',
        gdrive_link: quote.gdrive_link || '',
        tracking_numbers: quote.tracking_numbers || [],
        skus: quote.skus || [],
        subtotals: quote.subtotals || [],
      });
    } else if (v4Quote) {
      // Seed once per quote from V4. A refetch of the same quote (same key,
      // already V4-seeded) must not clobber the user's unsaved edits; a quote
      // seeded from V3 first — the shadow loads while the PG detail is still in
      // flight — must still upgrade to V4 once it arrives, or the partial
      // shadow's values would stick.
      const editKey = v4Quote.quoteNumber || v4Quote.mongoQuoteId || v4Quote.id || null;
      if (seededQuoteKeyRef.current === editKey && seededQuoteSourceRef.current === 'v4') {
        return;
      }
      seededQuoteKeyRef.current = editKey;
      seededQuoteSourceRef.current = 'v4';

      // Reuse the shared mapper, then layer on the initialCustomerData fallbacks
      // the edit flow relies on for customer/sales/contractual manager.
      setFormData({
        ...mapV4DetailToFormData(v4Quote, initialCustomerData?.customerId || ''),
        sales_manager: v4Quote.salesManagerId || initialCustomerData?.salesManagerId || '',
        contractual_manager: v4Quote.contractualManagerId || initialCustomerData?.contractualManagerId || '',
      });
    } else if (duplicateFrom) {
      // Duplicate: seed a brand-new quote from an existing one. Copy the
      // commercial content (customer, line items, managers, terms, project) but
      // reset identity + lifecycle fields so this is a fresh quote, not a copy
      // of the original's state. Mirrors the create branch's two-phase init so
      // the freshly assigned quote number lands once it loads.
      setFormData((prev) => {
        if (!createFormInitializedRef.current) {
          createFormInitializedRef.current = true;
          return {
            ...mapV4DetailToFormData(duplicateFrom, duplicateFrom.accountId),
            quote_number: nextQuoteNumber || '',
            quote_version: 1,
            status: 'preparation',
            ...getDefaultQuoteDates(),
            estimated_delivery: '',
            ticket: '',
            po_number: '',
            next_step: '',
            next_step_deadline: '',
            internal_notes: '',
            hubspot_link: '',
            gdrive_link: '',
            tracking_numbers: [],
          };
        }
        return { ...prev, quote_number: prev.quote_number || nextQuoteNumber || '' };
      });
    } else {
      setFormData((prev) => {
        if (!createFormInitializedRef.current) {
          createFormInitializedRef.current = true;
          return {
            ...createDefaultQuoteForm(nextQuoteNumber || ''),
            customer: initialCustomerData?.customerId || matchedCustomerId || '',
            sales_manager: initialCustomerData?.salesManagerId || '',
            contractual_manager: initialCustomerData?.contractualManagerId || '',
            contract: initialCustomerData?.contractId || '',
          };
        }

        const defaultDates = getDefaultQuoteDates();
        return {
          ...prev,
          quote_number: prev.quote_number || nextQuoteNumber || '',
          date: prev.date || defaultDates.date,
          expiration_date: prev.expiration_date || defaultDates.expiration_date,
        };
      });
    }
  }, [v4Quote, quote, duplicateFrom, open, nextQuoteNumber]);

  // Dirty-state tracking for unsaved changes detection
  const initialFormDataRef = useRef<FormData>(cloneFormData(DEFAULT_FORM));
  const [snapshotReady, setSnapshotReady] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // #883 — zero-total save confirmation. When Save is pressed on a quote whose
  // priced lines net to $0, the built payload is parked here until the user
  // confirms. Once confirmed in an editing session, later saves skip the
  // prompt (the intent was already acknowledged; a failed save retry must
  // not re-ask).
  const zeroTotalAcknowledged = useRef(false);
  const [zeroTotalPending, setZeroTotalPending] = useState<{ closeAfterSave: boolean; data: Record<string, unknown> } | null>(null);

  useEffect(() => {
    latestFormDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (open) {
      // Delay one tick to let the form populate from quote data
      const timer = setTimeout(() => {
        initialFormDataRef.current = cloneFormData(latestFormDataRef.current);
        setSnapshotReady(true);
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setSnapshotReady(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quote?._id, v4Quote?.id]);

  const dirtyFieldCount = useMemo(() => {
    if (!snapshotReady) return 0;
    const initial = initialFormDataRef.current;
    let count = 0;
    for (const key of Object.keys(initial) as (keyof FormData)[]) {
      const curr = formData[key];
      const init = initial[key];
      if (Array.isArray(curr) && Array.isArray(init)) {
        if (JSON.stringify(curr) !== JSON.stringify(init)) count++;
      } else if (curr !== init) {
        count++;
      }
    }
    return count;
  }, [formData, snapshotReady]);

  const isDirty = dirtyFieldCount > 0;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onOpenChange(newOpen);
  };

  const validateQuoteForm = () => {
    // On create, the server always assigns quote_number — skip the duplicate
    // check so a pre-seeded suggestion that happens to collide doesn't block submit.
    if (isEditing && quoteNumberStatus === 'duplicate') {
      toast.error('Quote number already exists. Please use a different number.');
      return false;
    }
    if (!(formData.customer || customerPgAccountId || customerMongoOrgId)) {
      toast.error('Account is required');
      return false;
    }
    // NO legacy-organisation guard. A quote only needs `account_id` (PostgreSQL);
    // `quotes` has no Mongo organisation column at all. Sub-accounts can never own
    // one — accounts_mongo_organisation_id_key is UNIQUE — so this guard made 39% of
    // live customer accounts permanently un-quotable, including every Acces Pharma,
    // McGill, Groupe Innovo and JGH branch.
    //
    // The legacy V1 mirror is skipped server-side for these accounts (see
    // createMongoBackingForQuote in rest_server/routes/v4/quotes.js, which reads the
    // account's OWN mongo_organisation_id and returns null rather than inheriting the
    // parent's). That is the safety interlock: without it, removing this guard would
    // file the quote under the PARENT's name on a customer-facing legacy document.
    // Do not ship this change ahead of that one.
    if (!formData.project_name.trim()) {
      toast.error('Project name is required');
      return false;
    }
    if (!formData.sales_manager) {
      toast.error('Account manager is required');
      return false;
    }
    return true;
  };

  // Build a properly transformed submit payload from formData.
  // Resolves contract PG UUID → MongoDB ObjectId, transforms shipping fields,
  // normalises financial fields, and strips immutable fields when editing.
  const buildQuoteSubmitData = (): Record<string, unknown> => {
    const updatedSkus = buildUpdatedSkus(formData.skus, { stripInvalidMongoIds: true });
    const updatedSubtotals = buildUpdatedSubtotals(updatedSkus, formData.subtotals);

    // Drop empty fields, but send a CLEARED optional reference field (Ticket,
    // Contractual Manager, Price List — set to '' by their "None" option) as null
    // so the update actually clears it; a stripped '' never reached the backend
    // and the old value reappeared on reopen (#100102). skus/subtotals empty
    // arrays are kept so "remove all items" persists; shipping is transformed below.
    const data = collectQuoteSubmitFields(formData as unknown as Record<string, unknown>, {
      sendEmptyArrays: new Set(['skus', 'subtotals']),
      skip: new Set(['shipping_site', 'shipping_address']),
    });

    // Customer field: resolve PG account UUID -> MongoDB organisation id for quote storage
    if (customerMongoOrgId) {
      data.customer = customerMongoOrgId;
    } else {
      delete data.customer;
    }

    // Preserve the PG subscription UUID for the V4 create payload BEFORE the
    // contract field is rewritten to the MongoDB ObjectId below.
    // On create, toCreateQuoteV4Payload reads data.subscriptionId; on edit the
    // legacy V3 path uses data.contract (MongoDB ObjectId), so this is additive.
    if (data.contract) {
      data.subscriptionId = data.contract as string;
    }

    // Contract field: resolve PG UUID to MongoDB ObjectId for quote storage
    // Quotes live in MongoDB and expect a MongoDB ObjectId for the contract reference
    if (data.contract) {
      const selectedContract = contracts.find(c => c._id === data.contract);
      if (selectedContract?.mongoContractId) {
        data.contract = selectedContract.mongoContractId;
      } else {
        // PostgreSQL-only contract — no MongoDB ObjectId exists, omit to avoid cast error
        delete data.contract;
      }
    }

    // Don't send quote_number/version when editing (immutable)
    if (isEditing) {
      delete data.quote_number;
      delete data.quote_version;
    }

    // Transform flat shipping fields → shipping_addresses array (matches schema).
    // Only emit the array when the form actually holds shipping data. Sending an
    // empty [] on an edit WIPES the quote's existing shipping address/site — the
    // legacy write replaces the array wholesale and the V4 write nulls the
    // address — the same "save wipes what the form didn't send" class as the
    // line items. When there's nothing, omit the key so existing data survives.
    if (formData.shipping_site || formData.shipping_address) {
      data.shipping_addresses = [{
        site: formData.shipping_site || undefined,
        name: allSites.find((s) => s.mongoSiteId === formData.shipping_site)?.name || '',
        address: formData.shipping_address || '',
      }];
    } else {
      delete data.shipping_addresses;
    }

    data.skus = updatedSkus;
    data.subtotals = updatedSubtotals;
    data.total = calculateQuoteTotal(updatedSkus);

    return data;
  };

  // Resolve contract PG UUID → MongoDB ObjectId for the invoice payload
  const resolveContractMongoId = (): string | undefined => {
    if (!formData.contract) return undefined;
    const selectedContract = contracts.find(c => c._id === formData.contract);
    return selectedContract?.mongoContractId || undefined;
  };

  const saveQuoteForm = async ({
    closeAfterSave,
    prebuiltData,
    skipZeroTotalConfirm = false,
  }: {
    closeAfterSave: boolean;
    prebuiltData?: Record<string, unknown>;
    skipZeroTotalConfirm?: boolean;
  }) => {
    // New-customer create is a separate, lighter contract (customer name
    // only, no account/customer picker) — skip the normal account-required
    // validation and payload building entirely.
    if (!isEditing && newCustomerMode && onCreateForNewCustomer) {
      if (!newCustomerName.trim()) {
        toast.error(t('form.newCustomerRequired', 'Customer name is required'));
        return false;
      }
      // The backend get-or-creates the ACCOUNT by name, but always inserts a
      // NEW quote row — there is no quote-level dedup. Unlike the
      // existing-customer path (which captures the created PG id and routes
      // later keep-open saves to an update), this lighter contract has no
      // account/customer selection to build an update payload from. Guard
      // against a second submit instead: block re-entry and always close the
      // modal on success, regardless of which Save button was clicked, so a
      // second Save is never possible.
      if (newCustomerSubmittedRef.current) return false;
      newCustomerSubmittedRef.current = true;
      try {
        await onCreateForNewCustomer({
          newCustomer: { name: newCustomerName.trim() },
          projectName: formData.project_name || undefined,
          description: formData.description || undefined,
          status: formData.status,
          salesManagerId: formData.sales_manager || undefined,
        });
        onOpenChange(false);
        return true;
      } catch {
        // Error handled by the mutation hook's toast. Allow retry.
        newCustomerSubmittedRef.current = false;
        return false;
      }
    }
    if (!validateQuoteForm()) return false;
    // #883 — parking the payload here (not just a boolean) lets the confirmed
    // retry submit EXACTLY what the user saw when the dialog opened; the form
    // is modal-blocked in between, so nothing can change underneath it.
    const data = prebuiltData ?? buildQuoteSubmitData();
    if (!skipZeroTotalConfirm && !zeroTotalAcknowledged.current && needsZeroTotalConfirmation(data)) {
      setZeroTotalPending({ closeAfterSave, data });
      return false;
    }
    try {
      const submitResult = await onSubmit(data as CreateQuoteInput | UpdateQuoteInput);
      // onSubmit resolves to the created quote on a create (void on update).
      const createdQuote = submitResult as V3Quote | undefined;

      // First create with sensors staged before the quote existed: attach them
      // now using the server-assigned quote_number (a client-supplied number is
      // ignored server-side). Each add is independent — keep going on partial
      // failure and report the count.
      if (!isEditing && !createdQuoteNumber && createdQuote?.quote_number && stagedSensors.length > 0) {
        const qn = createdQuote.quote_number;
        const outcomes = await Promise.allSettled(
          stagedSensors.map((s) => addSensorToQuote(s._id, qn))
        );
        const attached = stagedSensors.filter((_, i) => outcomes[i].status === 'fulfilled');
        const failedCount = outcomes.length - attached.length;
        // Seed the cache so the saved sensors appear immediately when the
        // section switches to live mode, then refetch the source of truth.
        queryClient.setQueryData([...quoteKeys.sensors(qn), 'byQuote'], attached);
        queryClient.invalidateQueries({ queryKey: quoteKeys.sensors(qn) });
        if (failedCount > 0) {
          toast.error(t('sensors.attachFailed', { count: failedCount }));
        }
      }
      // Remember the new quote_number so further sensor edits go live (handles
      // the keep-open "Save" button); clear the staged list either way.
      if (!isEditing && createdQuote?.quote_number) {
        setCreatedQuoteNumber(createdQuote.quote_number);
      }
      setStagedSensors([]);

      initialFormDataRef.current = cloneFormData(formData);
      if (closeAfterSave) {
        onOpenChange(false);
      } else {
        toast.success(t('toast.updateSuccess'));
      }
      return true;
    } catch {
      // Error handled by mutation hook
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const closeAfterSave = closeAfterSaveRef.current;
    await saveQuoteForm({ closeAfterSave });
    closeAfterSaveRef.current = true;
  };

  // "Save anyway" on the $0-total confirmation: resume the parked save with
  // its exact payload and remember the acknowledgment for this session.
  const confirmZeroTotalSave = async () => {
    const pending = zeroTotalPending;
    setZeroTotalPending(null);
    if (!pending) return;
    zeroTotalAcknowledged.current = true;
    await saveQuoteForm({
      closeAfterSave: pending.closeAfterSave,
      prebuiltData: pending.data,
      skipZeroTotalConfirm: true,
    });
  };

  const handlePrintQuote = async () => {
    if (!pgQuoteId) {
      toast.error(t('form.saveFirst'));
      return;
    }

    if (isDirty) {
      const saved = await saveQuoteForm({ closeAfterSave: false });
      if (!saved) return;
    }

    try {
      const response = await fetch(getQuoteHtmlUrlV4(pgQuoteId), {
        credentials: 'include',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Failed to load quote HTML (${response.status})`);
      const html = await response.text();
      await printHtmlDocument(
        html,
        `ATEK Quote ${formData.quote_number}-${formData.quote_version} ${formData.project_name}.pdf`,
      );
    } catch (error) {
      toast.error(t('toast.pdfError'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Handle customer change - clear cascaded fields
  const handleCustomerChange = (newCustomerId: string) => {
    const selectedOrg = customers.find((c) => c._id === newCustomerId);
    setFormData((prev) => ({
      ...prev,
      customer: newCustomerId,
      // Pre-fill the Account Manager from the chosen account's assigned AM so the required
      // field is satisfied on the ERP > Quotes "New Quote" flow (the account-page flow seeds
      // it via initialCustomerData; here the customer is picked inside the modal). The user
      // can still override it. If the account has no AM and the creator is an Account Manager,
      // use the creator as the default owner.
      sales_manager: resolveAccountManagerFormValue(selectedOrg?.salesManagerId),
      contract: '',
      ticket: '',
      contractual_manager: '',
      billing_site: '',
      billing_address: '',
      shipping_site: '',
      shipping_address: '',
    }));
  };

  // Auto-fill fields from contract (V4 PostgreSQL)
  const fillFromContract = async (contractId: string) => {
    const contract = await getSubscriptionDetail(contractId);
    if (!contract) return;

    if (contract.sales_manager) updateField('sales_manager', contract.sales_manager);
    if (contract.contractual_managers?.length) updateField('contractual_manager', contract.contractual_managers[0]);
    if (contract.payment_terms) updateField('payment_terms', contract.payment_terms as PaymentTerms);
    if (contract.language) updateField('language', contract.language as Language);
    if (contract.currency) updateField('currency', contract.currency as Currency);
  };

  // Handle tracking number changes
  const addTrackingNumber = () => {
    updateField('tracking_numbers', [...formData.tracking_numbers, '']);
  };

  const updateTrackingNumber = (index: number, value: string) => {
    const updated = [...formData.tracking_numbers];
    updated[index] = value;
    updateField('tracking_numbers', updated);
  };

  const removeTrackingNumber = (index: number) => {
    updateField('tracking_numbers', formData.tracking_numbers.filter((_, i) => i !== index));
  };

  // Sensor handlers. When the quote already has a number (editing, or just
  // created) we hit the live add/remove API; before that we stage picks in
  // local state and persist them on save.
  const handleAddSensor = async (sensor: QuoteSensor) => {
    // Belt-and-suspenders: the Sensors section is hidden in new-customer mode
    // (see sensorsDisabledForNewCustomer), but guard the handler too so it can
    // never stage a pick that would be silently dropped on submit.
    if (sensorsDisabledForNewCustomer) return;
    if (sensorsQuoteNumber) {
      await addSensorMutation.mutateAsync({ sensorId: sensor._id, quoteNumber: sensorsQuoteNumber, sensor });
      return;
    }
    setStagedSensors((prev) =>
      prev.some((s) => s._id === sensor._id)
        ? prev
        : [...prev, { ...sensor, invoices: sensor.invoices ?? [] }]
    );
  };

  const handleRemoveSensor = async (sensorId: string) => {
    if (sensorsQuoteNumber) {
      await removeSensorMutation.mutateAsync({ sensorId, quoteNumber: sensorsQuoteNumber });
      return;
    }
    setStagedSensors((prev) => prev.filter((s) => s._id !== sensorId));
  };

  const handleRemoveAllSensors = async () => {
    if (sensorsQuoteNumber) {
      await Promise.all(
        quoteSensors.map((s) =>
          removeSensorMutation.mutateAsync({ sensorId: s._id, quoteNumber: sensorsQuoteNumber })
        )
      );
      return;
    }
    setStagedSensors([]);
  };

  // New Invoice from Quote
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

  // Invoicing still runs through the legacy pair: it needs the quote's legacy id
  // AND the account's OWN organisation. Deliberately not pickerOrgId — billing a
  // sub-account under its parent's organisation is the mis-attribution the
  // read-only fallback above exists to avoid. A PG-only quote has neither, so the
  // action genuinely cannot run; this is the reason shown instead of nothing.
  const invoiceUnavailableReason = (!mongoQuoteId || !customerMongoOrgId)
    ? t(
      'actions.invoiceUnavailable',
      'This customer account is not set up for invoicing yet. Ask an administrator to finish setting up the account, then create the invoice.',
    )
    : undefined;

  const handleCreateInvoice = async () => {
    // Same condition spelled out again rather than `if (invoiceUnavailableReason)`
    // so TypeScript keeps narrowing both ids to strings for the payload below.
    if (!mongoQuoteId || !customerMongoOrgId) {
      // The buttons stay clickable on purpose: the shared Button sets
      // disabled:pointer-events-none, so a disabled button shows no tooltip —
      // the explanation has to survive the click, or this is a silent dead end
      // again in a different costume.
      toast.error(t('actions.invoiceUnavailableTitle', 'Cannot create an invoice yet'), {
        description: invoiceUnavailableReason,
      });
      return;
    }
    // Multiple invoices per quote are allowed (deposits, re-billing), but warn
    // when the quote is already fully invoiced so it's not done by accident.
    if (formData.status === 'invoiced' && !window.confirm(t('actions.alreadyInvoicedWarning'))) {
      return;
    }
    setIsCreatingInvoice(true);
    try {
      // Step 1: Save pending changes using the same transformations as handleSubmit
      const quoteData = buildQuoteSubmitData();
      await onSubmit(quoteData as UpdateQuoteInput);

      const updatedSkus = buildUpdatedSkus(formData.skus);
      const invoiceSkus = filterInvoiceableSkus(updatedSkus);
      const updatedSubtotals = buildUpdatedSubtotals(invoiceSkus, formData.subtotals);
      const total = calculateQuoteTotal(invoiceSkus);

      // Step 2: Build invoice payload from formData (current edited state, just saved)
      // Use resolved MongoDB ObjectId for contract (same resolution as quote save)
      const resolvedContract = resolveContractMongoId();
      // Due date is derived from the payment terms (e.g. net-30 → 30 days after
      // the invoice date), matching the legacy invoice behavior. on-order/net-0
      // → due same day.
      const invoiceDate = new Date();
      const netDays = parseInt(String(formData.payment_terms).replace(/\D/g, ''), 10) || 0;
      const invoicePayload: Record<string, unknown> = {
        customer: customerMongoOrgId,
        contract: resolvedContract,
        sales_manager: formData.sales_manager,
        contractual_manager: formData.contractual_manager,
        project_name: formData.project_name,
        project_number: formData.project_number,
        description: formData.description,
        comment: formData.comment,
        internal_notes: formData.internal_notes,
        po_number: formData.po_number,
        payment_terms: formData.payment_terms,
        currency: formData.currency,
        language: formData.language,
        tax_category: formData.tax_category,
        pricelist: formData.pricelist || undefined,
        subscription: formData.subscription || undefined,
        quote_number: formData.quote_number,
        billing_site: formData.billing_site,
        billing_address: formData.billing_address,
        shipping_addresses: formData.shipping_site || formData.shipping_address
          ? [{ site: formData.shipping_site || undefined, name: allSites.find((s) => s.mongoSiteId === formData.shipping_site)?.name || '', address: formData.shipping_address || '' }]
          : [],
        hubspot_link: formData.hubspot_link,
        gdrive_link: formData.gdrive_link,
        tracking_numbers: formData.tracking_numbers,
        subtotals: updatedSubtotals,
        total,
        quote: mongoQuoteId,
        status: 'preparation',
        date: invoiceDate.toISOString(),
        expiration_date: addDays(invoiceDate, netDays).toISOString(),
        invoice_version: 1,
        po_file: Array.isArray(quote?.po_file) ? quote.po_file[0] || null : quote?.po_file,
        skus: invoiceSkus.map((sku) => ({
          ...sku,
          ref_id: sku._id,
          _id: undefined,
        })),
      };

      if (sensorsToInvoice.length > 0) {
        invoicePayload.sensors = sensorsToInvoice;
      }

      // Step 3: Create the invoice
      const newInvoice = await createInvoice(invoicePayload as never);

      // Step 4: Determine quote status
      let newStatus = 'invoiced';
      if (
        quoteSensors.some(
          (sensor) =>
            !sensorsToInvoice.includes(sensor._id) &&
            (!sensor.invoices || sensor.invoices.length === 0)
        )
      ) {
        newStatus = 'partially-invoiced';
      }

      // Step 5: Update quote
      const updatedInvoiceNumbers = [...(quote?.invoice_numbers || [])];
      const newInvoiceNumber = (newInvoice as unknown as { invoice_number?: string }).invoice_number;
      if (newInvoiceNumber) {
        updatedInvoiceNumbers.push(newInvoiceNumber);
      }
      statusUpdateMutation.mutate({
        id: statusUpdateId || mongoQuoteId,
        data: { status: newStatus as UpdateQuoteInput['status'], invoice_numbers: updatedInvoiceNumbers },
      });

      // Step 6: Update local state and invalidate caches
      updateField('status', newStatus as FormData['status']);
      setSensorsToInvoice([]);
      refetchSensors();
      queryClient.invalidateQueries({ queryKey: quoteKeys.invoices(formData.quote_number) });

      // Step 7: Open the newly created invoice for review
      if (newInvoice._id) {
        window.open(`/erp/invoices?review=${encodeURIComponent(newInvoice._id)}`, '_blank');
      }

      toast.success(t('actions.createInvoice'));

      // Step 8: Let the caller advance the operations project out of "Invoicing"
      // once the quote is fully invoiced (partial invoicing stays in the stage).
      onInvoiceCreated?.(newStatus === 'invoiced');
    } catch (error) {
      toast.error('Failed to create invoice', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* A confirm/mini dialog layered on top of this form (the discard prompt,
          the site/user mini-forms) is dismissed by a click whose pointer-down
          Radix DEFERS to that click. By then the inner layer is gone and this
          dialog is the top layer again, so Radix dismisses it too — and for a
          dirty form handleOpenChange re-opens the discard prompt, which the next
          dismiss re-triggers, looping forever ("Cancel keeps popping up").
          Ignore any outside-interaction that originated inside another dialog
          surface; only a genuine click on the page background closes the form. */}
      <FixedSizeDialog
        size="6xl"
        scroll="section-scroll"
        onInteractOutside={(e) => {
          const target = (e.detail as { originalEvent?: Event } | undefined)?.originalEvent?.target;
          if (target instanceof Element && target.closest('[role="dialog"],[role="alertdialog"]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>
              {isEditing ? (
                <span className="flex items-center gap-2">
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                  #{formData.quote_number}
                  {formData.quote_version && (
                    <span className="text-sm font-normal text-muted-foreground">
                      v{formData.quote_version}
                    </span>
                  )}
                  {isDirty && (
                    <Badge variant="secondary" className="text-xs">
                      {dirtyFieldCount} {t('form.unsavedChanges')}
                    </Badge>
                  )}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {duplicateFrom ? t('form.duplicateTitle') : t('form.createTitle')}
                  {duplicateFrom?.quoteNumber && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {t('form.duplicatingFrom', { number: duplicateFrom.quoteNumber })}
                    </span>
                  )}
                  <div className="relative flex items-center">
                    <Input
                      value={formData.quote_number}
                      disabled
                      readOnly
                      placeholder="00000"
                      className="h-7 w-24 text-sm cursor-not-allowed opacity-60"
                      pattern="^\d{1,5}$"
                      maxLength={5}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">Assigned automatically when saved</span>
                  {isDirty && (
                    <Badge variant="secondary" className="text-xs">
                      {dirtyFieldCount} {t('form.unsavedChanges')}
                    </Badge>
                  )}
                </span>
              )}
            </DialogTitle>
            {isEditing && (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (!pgQuoteId) return;
                    window.open(getQuoteHtmlUrlV4(pgQuoteId), '_blank');
                  }}
                  disabled={!pgQuoteId}
                  title={t('form.viewPdf')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handlePrintQuote}
                  disabled={!pgQuoteId || isLoading}
                  title="PDF"
                >
                  <Printer className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!pgQuoteId) return;
                    window.open(getWorkOrderUrlV4(pgQuoteId), '_blank');
                  }}
                  disabled={!pgQuoteId}
                  title={t('form.workOrder')}
                >
                  <ClipboardList className="h-4 w-4 mr-1" />
                  {t('form.workOrder')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!pgQuoteId) return;
                    window.open(getPackingSlipUrlV4(pgQuoteId), '_blank');
                  }}
                  disabled={!pgQuoteId}
                  title={t('form.packingSlip')}
                >
                  <PackageCheck className="h-4 w-4 mr-1" />
                  {t('form.packingSlip')}
                </Button>
                {canCreateShippingLabel && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShippingLabelOpen(true)}
                    disabled={!pgQuoteId}
                    title={t('planete-courrier:actions.createLabel')}
                  >
                    <Truck className="h-4 w-4 mr-1" />
                    {t('planete-courrier:actions.createLabel')}
                  </Button>
                )}
                {canCreateShippingLabel && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRmaFromQuoteOpen(true)}
                    disabled={!pgQuoteId}
                    title={t('operations:rma.fromQuote.button', 'Create RMA')}
                  >
                    <Undo2 className="h-4 w-4 mr-1" />
                    {t('operations:rma.fromQuote.button', 'Create RMA')}
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogDescription className="sr-only">
            {isEditing ? t('form.editTitle') : t('form.createTitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div
            className={cn(
              'quote-form-scrollbar flex-1 overflow-y-auto pr-2',
              '[&_input:disabled]:border-slate-300 [&_input:disabled]:bg-slate-100 [&_input:disabled]:text-slate-600 [&_input:disabled]:shadow-inner [&_input:disabled]:opacity-100',
              '[&_textarea:disabled]:border-slate-300 [&_textarea:disabled]:bg-slate-100 [&_textarea:disabled]:text-slate-600 [&_textarea:disabled]:shadow-inner [&_textarea:disabled]:opacity-100',
              '[&_button:disabled]:border-slate-300 [&_button:disabled]:bg-slate-100 [&_button:disabled]:text-slate-600 [&_button:disabled]:shadow-inner [&_button:disabled]:opacity-100'
            )}
          >
            <div className="flex flex-col gap-5">

              {/* ============================================================ */}
              {/* SECTION: Details */}
              {/* ============================================================ */}
              <QuoteFormSection
                title={t('tabs.details')}
                icon={<FileText className="h-4 w-4" />}
                accent="green"
                className="order-2"
              >
                <div className="grid grid-cols-3 gap-4">
                  {/* Status */}
                  <div className="space-y-2">
                    <Label>{t('fields.status')}</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => updateField('status', v as QuoteStatus)}
                      disabled={!isFieldEditable('status')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Priority (operations triage — Urgent/Normal/Low) */}
                  <div className="space-y-2">
                    <Label>{t('fields.priority')}</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => updateField('priority', v as QuotePriority)}
                      disabled={!isFieldEditable('priority')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{t(`priorities.${p.value}`, p.label)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Deal Type */}
                  <div className="space-y-2">
                    <Label>{t('fields.dealType')}</Label>
                    <Select
                      value={formData.dealtype}
                      onValueChange={(v) => updateField('dealtype', v as DealType)}
                      disabled={!isFieldEditable('dealtype')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEALTYPE_OPTIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Probability */}
                  <div className="space-y-2">
                    <Label>{t('fields.probability')} (%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={formData.probability}
                      onChange={(e) => updateField('probability', Number(e.target.value))}
                      disabled={!isFieldEditable('probability')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <DatePickerField
                    label={t('fields.date')}
                    value={formData.date}
                    onChange={(v) => updateField('date', v)}
                    disabled={!isFieldEditable('date')}
                  />
                  <DatePickerField
                    label={t('fields.expirationDate')}
                    value={formData.expiration_date}
                    onChange={(v) => updateField('expiration_date', v)}
                    disabled={!isFieldEditable('expiration_date')}
                  />
                  <DatePickerField
                    label={t('fields.estimatedDelivery')}
                    value={formData.estimated_delivery}
                    onChange={(v) => updateField('estimated_delivery', v)}
                    disabled={!isFieldEditable('estimated_delivery')}
                  />
                  {isEditing && (
                    <div className="space-y-2">
                      <Label>{t('fields.acceptedDate')}</Label>
                      <Input
                        value={(quote?.accepted_at || v4Quote?.acceptedDate) ? format(parseISO((quote?.accepted_at || v4Quote?.acceptedDate)!), 'PPP') : '—'}
                        disabled
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Payment Terms */}
                  <div className="space-y-2">
                    <Label>{t('fields.paymentTerms')}</Label>
                    <Select
                      value={formData.payment_terms}
                      onValueChange={(v) => updateField('payment_terms', v as PaymentTerms)}
                      disabled={!isFieldEditable('payment_terms')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Currency */}
                  <div className="space-y-2">
                    <Label>{t('fields.currency')}</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(v) => updateField('currency', v as Currency)}
                      disabled={!isFieldEditable('currency')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Language */}
                  <div className="space-y-2">
                    <Label>{t('fields.language')}</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(v) => updateField('language', v as Language)}
                      disabled={!isFieldEditable('language')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map((l) => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tax Category */}
                  <div className="space-y-2">
                    <Label>{t('fields.taxCategory')}</Label>
                    <Select
                      value={formData.tax_category}
                      onValueChange={(v) => updateField('tax_category', v as TaxCategory)}
                      disabled={!isFieldEditable('tax_category')}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TAX_OPTIONS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {taxMismatch && (
                      <p className="text-xs text-warning">
                        ⚠ {t('form.taxMismatchWarning', {
                          tax: TAX_CATEGORIES[expectedTaxCategory!].label,
                          defaultValue: `Expected ${TAX_CATEGORIES[expectedTaxCategory!].label} for this billing destination. Verify the tax category.`,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </QuoteFormSection>

              {/* ============================================================ */}
              {/* SECTION: Account & Addresses */}
              {/* ============================================================ */}
              <QuoteFormSection
                title={t('tabs.customer')}
                icon={<Building2 className="h-4 w-4" />}
                accent="blue"
                className="order-1"
              >
                {!isEditing && onCreateForNewCustomer && (
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <input
                        id="quote-new-customer-toggle"
                        type="checkbox"
                        checked={newCustomerMode}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewCustomerMode(checked);
                          // A brand-new customer has no account yet for staged
                          // sensors to attach to; the Sensors section is hidden
                          // below, so clear any picks made before the toggle so
                          // they can't be lost silently on submit.
                          if (checked) setStagedSensors([]);
                        }}
                      />
                      <Label htmlFor="quote-new-customer-toggle" className="text-sm font-medium cursor-pointer">
                        {t('fields.newCustomer', 'New customer')}
                      </Label>
                    </div>
                    {newCustomerMode && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label htmlFor="quote-new-customer-name">{t('fields.customerName', 'Customer name')}</Label>
                          <Input id="quote-new-customer-name" value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {/* Account */}
                  {!newCustomerMode && (
                  <div className="space-y-2">
                    <Label>{t('fields.customer')} *</Label>
                    <Popover
                      open={customerOpen}
                      onOpenChange={(next) => {
                        setCustomerOpen(next);
                        // The search box is controlled, so its text would otherwise
                        // survive a dismiss and reopen onto a stale filtered list.
                        if (!next) setCustomerSearch('');
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={customerOpen}
                          disabled={!isFieldEditable('customer') || isCustomerLocked}
                          className={cn(
                            'w-full justify-between font-normal',
                            !selectedCustomer && !initialCustomerDisplay && 'text-muted-foreground'
                          )}
                        >
                          {isCustomerLocked && initialCustomerDisplay
                            ? initialCustomerDisplay
                            : selectedCustomer
                              ? getOrgDisplayName(selectedCustomer)
                              : initialCustomerDisplay || t('fields.selectCustomer')}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        {/* Filtered here rather than by cmdk: each row's value has
                            to be the account id (see below), and cmdk's own filter
                            scores the value too, which let the id's hex digits
                            match the search box. */}
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={t('fields.searchCustomer')}
                            value={customerSearch}
                            onValueChange={setCustomerSearch}
                          />
                          <CommandList>
                            <CommandEmpty>{t('fields.noCustomerFound')}</CommandEmpty>
                            <CommandGroup>
                              {customers
                                .filter((c: Organisation) => accountLabelMatchesSearch(getOrgDisplayName(c), customerSearch))
                                .map((c: Organisation) => {
                                const label = getOrgDisplayName(c);
                                return (
                                <CommandItem
                                  key={c._id}
                                  // Must be unique per account: cmdk treats a
                                  // duplicate value as the SAME item, so the 9
                                  // accounts named "Acces Pharma" highlighted
                                  // together and Enter/arrow keys always
                                  // resolved to the first one.
                                  value={c._id}
                                  onSelect={() => {
                                    handleCustomerChange(c._id);
                                    setCustomerOpen(false);
                                    setCustomerSearch('');
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      formData.customer === c._id || formData.customer === c.mongoOrgId
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                  {label}
                                </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  )}

                  {/* Contract */}
                  <div className="space-y-2">
                    <Label>{t('fields.contract')}</Label>
                    <div className="flex gap-1">
                      <Select
                        value={formData.contract || '__none__'}
                        onValueChange={(v) => {
                          const value = v === '__none__' ? '' : v;
                          updateField('contract', value);
                          // Auto-fill from contract (create mode)
                          if (value && !isEditing) {
                            fillFromContract(value);
                          }
                        }}
                        disabled={!isFieldEditable('contract') || !(customerMongoOrgId || customerPgAccountId)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={t('fields.selectContract')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">None</SelectItem>
                          {contracts.map((c) => (
                            <SelectItem key={c._id} value={c._id}>
                              {c.name} {c.contract_number ? `(${c.contract_number})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isEditing && formData.contract && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                          onClick={() => fillFromContract(formData.contract)}
                          title={t('actions.refreshFromContract')}>
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Account Manager — assignment dropdown: hide former AMs, but keep
                      the currently-selected one visible so an existing quote that points
                      at a former AM can still render and be re-assigned. */}
                  <div className="space-y-2">
                    <Label>{t('fields.salesManager')} *</Label>
                    <Select
                      value={formData.sales_manager}
                      // Ignore Radix's spurious onValueChange('') — the dropdown has no empty
                      // option, so '' only ever comes from Radix "correcting" a controlled value
                      // that isn't (yet) a rendered item (e.g. the account-seeded AM). Applying it
                      // would wipe the seed and re-block Save. Real selections are always non-empty.
                      onValueChange={(v) => { if (v) updateField('sales_manager', v); }}
                      disabled={!isFieldEditable('sales_manager')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select account manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {salesManagers
                          .filter((m) => !m.isFormer || m.mongoUserId === formData.sales_manager || m.id === formData.sales_manager)
                          .map((m) => (
                            <SelectItem key={m.id} value={m.mongoUserId || m.id}>
                              {m.name}{m.isFormer ? ` (${t('filters.former', 'Former')})` : ''}
                            </SelectItem>
                          ))}
                        {/* Fallback item for a currently-set AM that isn't in the global
                            Account Manager list.
                            Without a matching <SelectItem>, Radix treats the controlled value
                            as invalid and fires onValueChange('') on mount, wiping the seeded
                            AM and re-blocking Save. Rendering it here keeps the value valid. */}
                        {formData.sales_manager
                          && !salesManagers.some((m) => (m.mongoUserId || m.id) === formData.sales_manager)
                          && (
                            <SelectItem value={formData.sales_manager}>
                              {t('fields.assignedManager', 'Assigned account manager')}
                            </SelectItem>
                          )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contractual Manager */}
                  <div className="space-y-2">
                    <Label>{t('fields.contractualManager')}</Label>
                    <div className="flex gap-1">
                      <Popover open={contractualManagerOpen} onOpenChange={setContractualManagerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={contractualManagerOpen}
                            disabled={!isFieldEditable('contractual_manager') || !(customerMongoOrgId || customerPgAccountId)}
                            className={cn(
                              'w-full justify-between font-normal',
                              !selectedContractualManager && 'text-muted-foreground'
                            )}
                          >
                            {selectedContractualManager
                              ? getUserDisplayName(selectedContractualManager)
                              : t('fields.selectContact')}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t('fields.searchContact')} />
                            <CommandList>
                              <CommandEmpty>{t('fields.noContactFound')}</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  value="__none__"
                                  onSelect={() => {
                                    updateField('contractual_manager', '');
                                    setContractualManagerOpen(false);
                                  }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', !formData.contractual_manager ? 'opacity-100' : 'opacity-0')} />
                                  {t('fields.none')}
                                </CommandItem>
                                {orgUsers.map((u: User) => (
                                  <CommandItem
                                    key={u._id}
                                    value={getUserDisplayName(u)}
                                    onSelect={() => {
                                      updateField('contractual_manager', u._id);
                                      setContractualManagerOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        formData.contractual_manager === u._id ? 'opacity-100' : 'opacity-0'
                                      )}
                                    />
                                    {getUserDisplayName(u)}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                            disabled={!customerMongoOrgId}
                            title={t('contractualManager.menuTitle')}>
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setMiniUserOpen(true)}>
                            {t('contractualManager.menuCreateNew')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setAddExistingManagerOpen(true)}>
                            {t('contractualManager.menuAddExisting')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Ticket */}
                  <div className="space-y-2">
                    <Label>{t('fields.ticket')}</Label>
                    <Popover open={ticketOpen} onOpenChange={setTicketOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={ticketOpen}
                          disabled={!isFieldEditable('ticket') || !pickerOrgId}
                          className={cn(
                            'w-full justify-between font-normal',
                            !selectedTicket && 'text-muted-foreground'
                          )}
                        >
                          {selectedTicket
                            ? `${selectedTicket.ticket_number ? `#${selectedTicket.ticket_number} - ` : ''}${selectedTicket.name}`
                            : t('fields.none')}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder={t('fields.searchTicket')} />
                          <CommandList>
                            <CommandEmpty>{t('fields.noTicketsFound')}</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value={t('fields.none')}
                                onSelect={() => {
                                  updateField('ticket', '');
                                  setTicketOpen(false);
                                }}
                              >
                                <Check className={cn('mr-2 h-4 w-4', !formData.ticket ? 'opacity-100' : 'opacity-0')} />
                                {t('fields.none')}
                              </CommandItem>
                              {ticketsLoading && (
                                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                  {t('fields.loadingTickets')}
                                </div>
                              )}
                              {ticketsError && (
                                <div className="px-2 py-1.5 text-sm text-destructive">
                                  {t('fields.ticketLoadError')}
                                </div>
                              )}
                              {!ticketsLoading && !ticketsError && tickets.map((ticket) => {
                                const label = `${ticket.ticket_number ? `#${ticket.ticket_number} - ` : ''}${ticket.name}`;
                                return (
                                  <CommandItem
                                    key={ticket._id}
                                    value={label}
                                    onSelect={() => {
                                      updateField('ticket', ticket._id);
                                      setTicketOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        formData.ticket === ticket._id ? 'opacity-100' : 'opacity-0'
                                      )}
                                    />
                                    {label}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Pricelist */}
                  <div className="space-y-2">
                    <Label>{t('fields.pricelist')}</Label>
                    <Select
                      value={formData.pricelist || '__none__'}
                      onValueChange={(v) => updateField('pricelist', v === '__none__' ? '' : v)}
                      disabled={!isFieldEditable('pricelist')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('fields.selectPricelist')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('fields.none')}</SelectItem>
                        {pricelists.map((pl: Pricelist) => (
                          <SelectItem key={pl._id} value={pl._id}>
                            {pl.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subscription */}
                  <div className="space-y-2">
                    <Label>{t('fields.subscription')}</Label>
                    <Select
                      value={formData.subscription || '__none__'}
                      onValueChange={(v) => {
                        const subId = v === '__none__' ? '' : v;
                        updateField('subscription', subId);
                        // Auto-fill from subscription
                        if (subId) {
                          // If SKUs already exist, confirm before overwriting
                          if (formData.skus.length > 0) {
                            const confirmed = window.confirm(t('fields.subscriptionOverwrite'));
                            if (!confirmed) return;
                          }
                          const sub = subscriptions.find((s: Subscription) => s._id === subId);
                          if (sub) {
                            setFormData((prev) => ({
                              ...prev,
                              subscription: subId,
                              contractual_manager: sub.contractual_manager || prev.contractual_manager,
                              project_name: sub.project_name || sub.name || prev.project_name,
                              description: sub.description || prev.description,
                              internal_notes: sub.internal_notes || prev.internal_notes,
                              gdrive_link: sub.gdrive_link || prev.gdrive_link,
                              billing_site: sub.billing_site || prev.billing_site,
                              billing_address: sub.billing_address || prev.billing_address,
                              shipping_site: sub.shipping_addresses?.[0]?.site || prev.shipping_site,
                              shipping_address: sub.shipping_addresses?.[0]?.address || prev.shipping_address,
                              probability: 100,
                              language: sub.language || prev.language,
                              currency: sub.currency || prev.currency,
                              tax_category: sub.tax_category || prev.tax_category,
                              pricelist: sub.pricelist || prev.pricelist,
                              skus: sub.skus || prev.skus,
                              subtotals: sub.subtotals || prev.subtotals,
                            }));
                            toast.success(t('fields.subscriptionAutoFill'));
                          }
                        }
                      }}
                      disabled={!isFieldEditable('subscription') || !pickerOrgId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('fields.selectSubscription')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('fields.none')}</SelectItem>
                        {subscriptions.map((s: Subscription) => (
                          <SelectItem key={s._id} value={s._id}>
                            {s.sub_number ? `${s.sub_number} ` : ''}{s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Addresses */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label>{t('fields.billingSite')}</Label>
                    <div className="flex gap-1">
                      <SiteCombobox
                        sites={allSites}
                        suggestedTag="billing"
                        value={formData.billing_site || ''}
                        placeholder={t('fields.selectBillingSite')}
                        disabled={!isFieldEditable('billing_site') || !sitesPgAccountId}
                        testId="billing-site-picker"
                        onSelect={(siteId) => {
                          updateField('billing_site', siteId);
                          // Auto-fill billing address from site
                          const site = allSites.find((s) => s.mongoSiteId === siteId);
                          if (site) {
                            updateField('billing_address', composeSiteAddress(site));
                          }
                        }}
                      />
                      {/* Deliberately still gated on customerMongoOrgId, not pickerOrgId: the
                          pickers above only READ the family's addresses, but this creates one,
                          and MiniSiteFormDialog is handed customerMongoOrgId. Enabling it for an
                          account with no organisation of its own would either write under the
                          parent's name or against an empty id. Same for the shipping and
                          contractual-manager "+" buttons. */}
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                        onClick={() => { setMiniSiteTag('billing'); setEditingSiteData(null); setMiniSiteOpen(true); }}
                        disabled={!customerMongoOrgId}
                        title={t('miniForm.createSite')}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                      {formData.billing_site && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                          onClick={() => {
                            const site = allSites.find((s) => s.mongoSiteId === formData.billing_site);
                            setEditingSiteData(site ? { ...site } : null);
                            setMiniSiteTag('billing');
                            setMiniSiteOpen(true);
                          }}
                          title={t('miniForm.editSite')}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {selectedBillingSite?.email && (
                      <p className="text-sm text-muted-foreground">
                        {t('fields.billingEmail')}: {selectedBillingSite.email}
                      </p>
                    )}
                    <Textarea
                      placeholder={t('fields.billingAddress')}
                      value={formData.billing_address}
                      onChange={(e) => updateField('billing_address', e.target.value)}
                      disabled={!isFieldEditable('billing_address')}
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('fields.shippingSite')}</Label>
                    <div className="flex gap-1">
                      <SiteCombobox
                        sites={allSites}
                        suggestedTag="shipping"
                        value={formData.shipping_site || ''}
                        placeholder={t('fields.selectShippingSite')}
                        disabled={!isFieldEditable('shipping_site') || !sitesPgAccountId}
                        testId="shipping-site-picker"
                        onSelect={(siteId) => {
                          updateField('shipping_site', siteId);
                          // Auto-fill shipping address from site
                          const site = allSites.find((s) => s.mongoSiteId === siteId);
                          if (site) {
                            updateField('shipping_address', composeSiteAddress(site));
                          }
                        }}
                      />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                        onClick={() => { setMiniSiteTag('shipping'); setEditingSiteData(null); setMiniSiteOpen(true); }}
                        disabled={!customerMongoOrgId}
                        title={t('miniForm.createSite')}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                      {formData.shipping_site && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
                          onClick={() => {
                            const site = allSites.find((s) => s.mongoSiteId === formData.shipping_site);
                            setEditingSiteData(site ? { ...site } : null);
                            setMiniSiteTag('shipping');
                            setMiniSiteOpen(true);
                          }}
                          title={t('miniForm.editSite')}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <Textarea
                      placeholder={t('fields.shippingAddress')}
                      value={formData.shipping_address}
                      onChange={(e) => updateField('shipping_address', e.target.value)}
                      disabled={!isFieldEditable('shipping_address')}
                      rows={2}
                    />
                  </div>
                </div>
              </QuoteFormSection>

              {/* ============================================================ */}
              {/* SECTION: Items (SKU Editor) */}
              {/* ============================================================ */}
              <QuoteFormSection
                title={t('tabs.items')}
                icon={<Package className="h-4 w-4" />}
                accent="indigo"
                className="order-4"
              >
                <QuoteSkuEditor
                  skus={formData.skus}
                  subtotals={formData.subtotals}
                  onSkusChange={(skus) => updateField('skus', skus)}
                  onSubtotalsChange={(subtotals) => updateField('subtotals', subtotals)}
                  language={formData.language}
                  currency={formData.currency}
                  disabled={!isFieldEditable('skus')}
                  invoicedQuantities={invoicedQuantities}
                  pricelist={pricelists.find(p => p._id === formData.pricelist)}
                />
              </QuoteFormSection>

              {/* ============================================================ */}
              {/* SECTION: Sensors */}
              {/* ============================================================ */}
              <QuoteFormSection
                title={t('tabs.sensors')}
                icon={<Radio className="h-4 w-4" />}
                accent="teal"
                className="order-5"
              >
                {sensorsDisabledForNewCustomer ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Radio className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {t('sensors.newCustomerHint', 'Configure sensors after the customer is created.')}
                    </p>
                  </div>
                ) : canReadSensors || invoiceWorkflow ? (
                  <>
                    <QuoteSensorsTable
                      sensors={sensorsForDisplay}
                      transmitters={quoteTransmitters}
                      sensorsToInvoice={sensorsToInvoice}
                      onSensorsToInvoiceChange={setSensorsToInvoice}
                      onSelectSensors={() => setSensorSelectOpen(true)}
                      onRemoveSensor={handleRemoveSensor}
                      onRemoveAllSensors={handleRemoveAllSensors}
                      onCreateInvoice={(canCreateInvoice || invoiceWorkflow) && (
                        invoiceWorkflow
                        || formData.status === 'invoice-ready'
                        || formData.status === 'partially-invoiced'
                      ) ? handleCreateInvoice : undefined}
                      createInvoiceUnavailableReason={invoiceUnavailableReason}
                      quoteStatus={formData.status}
                      hasCustomer={!!(customerMongoOrgId || customerPgAccountId)}
                      isCreatingInvoice={isCreatingInvoice}
                      isLoading={sensorsLoading}
                    />
                    <SensorSelectDialog
                      open={sensorSelectOpen}
                      onOpenChange={setSensorSelectOpen}
                      customerId={customerMongoOrgId || ''}
                      pgAccountId={customerPgAccountId}
                      quoteSensorIds={quoteSensorIds}
                      onAddSensor={handleAddSensor}
                      onRemoveSensor={handleRemoveSensor}
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Radio className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {t('sensors.noAccess')}
                    </p>
                  </div>
                )}
              </QuoteFormSection>

              {/* ============================================================ */}
              {/* SECTION: Project */}
              {/* ============================================================ */}
              <QuoteFormSection
                title={t('tabs.project')}
                icon={<FolderOpen className="h-4 w-4" />}
                accent="amber"
                className="order-3"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fields.projectName')} *</Label>
                    <Input
                      value={formData.project_name}
                      onChange={(e) => updateField('project_name', e.target.value)}
                      placeholder="e.g., Cold Storage Monitoring"
                      disabled={!isFieldEditable('project_name')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fields.projectNumber')}</Label>
                    <Popover open={projectNumberOpen} onOpenChange={setProjectNumberOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={projectNumberOpen}
                          disabled={!isFieldEditable('project_number')}
                          className={cn(
                            'w-full justify-between font-normal h-9 text-sm',
                            !formData.project_number && 'text-muted-foreground'
                          )}
                        >
                          {formData.project_number || t('fields.selectProjectNumber')}
                          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={t('fields.searchProject')}
                            value={projectNumberSearch}
                            onValueChange={setProjectNumberSearch}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {projectNumberSearch.trim() ? (
                                <CommandItem
                                  onSelect={() => {
                                    updateField('project_number', projectNumberSearch.trim());
                                    setProjectNumberOpen(false);
                                    setProjectNumberSearch('');
                                  }}
                                  className="justify-center"
                                >
                                  {t('fields.noProjectFound')}: &quot;{projectNumberSearch.trim()}&quot;
                                </CommandItem>
                              ) : (
                                t('fields.noProjectFound')
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {projectNumbers
                                .filter((pn) => !projectNumberSearch || pn.toLowerCase().includes(projectNumberSearch.toLowerCase()))
                                .map((pn) => (
                                  <CommandItem
                                    key={pn}
                                    value={pn}
                                    onSelect={() => {
                                      updateField('project_number', pn);
                                      setProjectNumberOpen(false);
                                      setProjectNumberSearch('');
                                    }}
                                  >
                                    <Check className={cn('mr-2 h-4 w-4', formData.project_number === pn ? 'opacity-100' : 'opacity-0')} />
                                    {pn}
                                  </CommandItem>
                                ))}
                              {projectNumberSearch.trim() && !projectNumbers.includes(projectNumberSearch.trim()) && (
                                <CommandItem
                                  value={`__create__${projectNumberSearch.trim()}`}
                                  onSelect={() => {
                                    updateField('project_number', projectNumberSearch.trim());
                                    setProjectNumberOpen(false);
                                    setProjectNumberSearch('');
                                  }}
                                >
                                  <Plus className="mr-2 h-4 w-4" />
                                  {projectNumberSearch.trim()}
                                </CommandItem>
                              )}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fields.poNumber')}</Label>
                    <Input
                      value={formData.po_number}
                      onChange={(e) => updateField('po_number', e.target.value)}
                      placeholder="Purchase order number"
                      disabled={!isFieldEditable('po_number')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fields.poFile')}</Label>
                    {isEditing && quote?.po_file ? (
                      <div className="flex items-center gap-2">
                        <a
                          href={`/api/v4/legacy/v2/files/${typeof quote.po_file === 'object' ? (quote.po_file as { _id: string })._id : quote.po_file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-atek-blue hover:underline truncate"
                        >
                          {typeof quote.po_file === 'object' && (quote.po_file as { name?: string }).name
                            ? (quote.po_file as { name: string }).name
                            : t('fields.poFile')}
                        </a>
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => deletePoFileMutation.mutate(quote._id)}
                          disabled={deletePoFileMutation.isPending}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : isEditing ? (
                      <div>
                        <input
                          ref={poFileInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file && quote) {
                              uploadPoFileMutation.mutate({ quoteId: quote._id, file });
                            }
                            // Reset input so the same file can be selected again
                            e.target.value = '';
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => poFileInputRef.current?.click()}
                          disabled={uploadPoFileMutation.isPending}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          {t('fields.uploadPoFile')}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t('fields.noPoFile')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('fields.description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Quote description..."
                    disabled={!isFieldEditable('description')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('fields.internalNotes')}</Label>
                  <Textarea
                    value={formData.internal_notes}
                    onChange={(e) => updateField('internal_notes', e.target.value)}
                    placeholder="Internal notes (not visible to customer)"
                    disabled={!isFieldEditable('internal_notes')}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('fields.comment')}</Label>
                  <Textarea
                    value={formData.comment}
                    onChange={(e) => updateField('comment', e.target.value)}
                    placeholder="Additional comments"
                    disabled={!isFieldEditable('comment')}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fields.nextStep')}</Label>
                    <Textarea
                      value={formData.next_step}
                      onChange={(e) => updateField('next_step', e.target.value)}
                      placeholder="Next action item"
                      disabled={!isFieldEditable('next_step')}
                      rows={2}
                    />
                  </div>
                  <DatePickerField
                    label={t('fields.nextStepDeadline')}
                    value={formData.next_step_deadline}
                    onChange={(v) => updateField('next_step_deadline', v)}
                    disabled={!isFieldEditable('next_step_deadline')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('fields.hubspotLink')}</Label>
                    <div className="flex gap-2">
                      <Input
                        className="flex-1"
                        value={formData.hubspot_link}
                        onChange={(e) => updateField('hubspot_link', e.target.value)}
                        placeholder="https://app.hubspot.com/..."
                        disabled={!isFieldEditable('hubspot_link')}
                      />
                      {formData.hubspot_link && isEditing && (
                        <Button type="button" variant="outline" size="sm"
                          onClick={() => {
                            if (!mongoQuoteId || !hubspotDealId) return;
                            hubspotDealSync.mutate({ quoteId: mongoQuoteId, hsId: hubspotDealId });
                          }}
                          disabled={!hubspotDealId || hubspotDealSync.isPending}>
                          {hubspotDealSync.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          <span className="ml-1">{t('actions.syncHubspot')}</span>
                        </Button>
                      )}
                    </div>
                    {formData.hubspot_link && isEditing && !hubspotDealId && (
                      <p className="text-xs text-muted-foreground">
                        {t('actions.syncHubspotNoDealId')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fields.gdriveLink')}</Label>
                    <Input
                      value={formData.gdrive_link}
                      onChange={(e) => updateField('gdrive_link', e.target.value)}
                      placeholder="https://drive.google.com/..."
                      disabled={!isFieldEditable('gdrive_link')}
                    />
                  </div>
                </div>

                {/* Tracking Numbers */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t('fields.trackingNumbers')}</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTrackingNumber}
                      disabled={!isFieldEditable('tracking_numbers')}
                    >
                      Add
                    </Button>
                  </div>
                  {formData.tracking_numbers.map((num, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={num}
                        onChange={(e) => updateTrackingNumber(i, e.target.value)}
                        placeholder="Tracking number"
                        disabled={!isFieldEditable('tracking_numbers')}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTrackingNumber(i)}
                        disabled={!isFieldEditable('tracking_numbers')}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Invoice Numbers (edit mode only) */}
                {isEditing && (
                  <div className="space-y-2">
                    <Label>{t('fields.invoiceNumbers')}</Label>
                    {linkedInvoices.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {linkedInvoices.map((inv) => (
                          <a
                            key={inv._id}
                            href={`/erp/invoices?highlight=${inv.invoice_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-sm hover:bg-muted/80"
                          >
                            #{inv.invoice_number}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('fields.noInvoices')}</p>
                    )}
                    {/* Untracked invoice numbers (on quote but not matched to real invoices) */}
                    {(() => {
                      const trackedNumbers = new Set(linkedInvoices.map(inv => inv.invoice_number));
                      const untrackedNumbers = (quote?.invoice_numbers ?? []).filter(num => !trackedNumbers.has(num));
                      if (untrackedNumbers.length === 0) return null;
                      return (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground mb-1">{t('fields.untrackedInvoices')}</p>
                          <div className="flex flex-wrap gap-1">
                            {untrackedNumbers.map(num => (
                              <Badge key={num} variant="outline" className="gap-1">
                                {num}
                                <button
                                  type="button"
                                  className="ml-1 hover:text-destructive"
                                  onClick={() => {
                                    const updated = (quote?.invoice_numbers ?? []).filter(n => n !== num);
                                    statusUpdateMutation.mutate({ id: quote!._id, data: { invoice_numbers: updated } as UpdateQuoteInput });
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </QuoteFormSection>

              {/* ============================================================ */}
              {/* SECTION: Notes */}
              {/* ============================================================ */}
              {isEditing && (
                <QuoteFormSection
                  title={t('tabs.notes')}
                  icon={<MessageSquare className="h-4 w-4" />}
                  accent="slate"
                  className="order-6"
                >
                  <QuoteNotes quoteId={mongoQuoteId || ''} notes={quote?.notes || []} />
                </QuoteFormSection>
              )}

              {/* ============================================================ */}
              {/* SECTION: History (Changelog) */}
              {/* ============================================================ */}
              {isEditing && (
                <QuoteFormSection
                  title={t('tabs.history')}
                  icon={<History className="h-4 w-4" />}
                  accent="purple"
                  className="order-7"
                >
                  <QuoteChangelog quoteId={mongoQuoteId || ''} />
                </QuoteFormSection>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            {/* Status workflow quick-actions */}
            {isEditing && (
              <div className="flex gap-2 mr-auto">
                {formData.status === 'preparation' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      statusUpdateId && statusUpdateMutation.mutate({ id: statusUpdateId, data: { status: 'sent' } });
                      updateField('status', 'sent');
                    }}
                    disabled={statusUpdateMutation.isPending}
                  >
                    {t('workflow.send')}
                  </Button>
                )}
                {formData.status === 'sent' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const now = format(new Date(), 'yyyy-MM-dd');
                      statusUpdateId && statusUpdateMutation.mutate({
                        id: statusUpdateId,
                        data: { status: 'approved', accepted_at: now, probability: 100 },
                      });
                      updateField('status', 'approved');
                    }}
                    disabled={statusUpdateMutation.isPending}
                  >
                    {t('workflow.approve')}
                  </Button>
                )}
                {formData.status === 'approved' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      statusUpdateId && statusUpdateMutation.mutate({ id: statusUpdateId, data: { status: 'inprogress' } });
                      updateField('status', 'inprogress');
                    }}
                    disabled={statusUpdateMutation.isPending}
                  >
                    {t('workflow.start')}
                  </Button>
                )}
                {formData.status === 'inprogress' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      statusUpdateId && statusUpdateMutation.mutate({ id: statusUpdateId, data: { status: 'invoice-ready' } });
                      updateField('status', 'invoice-ready');
                    }}
                    disabled={statusUpdateMutation.isPending}
                  >
                    {t('workflow.readyForInvoice')}
                  </Button>
                )}
                {(canCreateInvoice || invoiceWorkflow) && (
                  invoiceWorkflow
                  || formData.status === 'invoice-ready'
                  || formData.status === 'partially-invoiced'
                ) && (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={handleCreateInvoice}
                    disabled={isCreatingInvoice || statusUpdateMutation.isPending}
                    title={invoiceUnavailableReason}
                  >
                    {isCreatingInvoice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isCreatingInvoice ? t('actions.creatingInvoice') : t('actions.createInvoice')}
                  </Button>
                )}
                {(['approved', 'inprogress', 'waiting-po', 'invoice-ready', 'partially-invoiced', 'invoiced'] as QuoteStatus[]).includes(formData.status as QuoteStatus) && (
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => window.open(`/orders?id=${mongoQuoteId}`, '_blank')}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    {t('actions.viewOrder')}
                  </Button>
                )}
                {formData.status !== 'cancelled' && formData.status !== 'preparation' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      statusUpdateId && statusUpdateMutation.mutate({ id: statusUpdateId, data: { status: 'cancelled' } });
                      updateField('status', 'cancelled');
                    }}
                    disabled={statusUpdateMutation.isPending}
                  >
                    {t('workflow.cancel')}
                  </Button>
                )}
              </div>
            )}
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('form.cancel')}
            </Button>
            <Button type="submit" variant="outline" disabled={isLoading}
              onClick={() => { closeAfterSaveRef.current = false; }}>
              {t('form.save')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('form.saveAndClose')}
            </Button>
          </DialogFooter>
        </form>
      </FixedSizeDialog>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitleComponent>{t('form.discardTitle')}</AlertDialogTitleComponent>
            <AlertDialogDescription>{t('form.discardMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('form.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowDiscardDialog(false); onOpenChange(false); }}>
              {t('form.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* #883 — soft guard: the form's priced lines total $0 (typically a 100%
          discount on every line). Same nested-alert-dialog pattern as the
          discard prompt above — the parent dialog's onInteractOutside already
          ignores interactions originating inside an alertdialog surface. */}
      <AlertDialog open={zeroTotalPending !== null} onOpenChange={(open) => { if (!open) setZeroTotalPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitleComponent>{t('form.zeroTotalTitle', 'Save this quote at $0.00?')}</AlertDialogTitleComponent>
            <AlertDialogDescription>
              {t('form.zeroTotalMessage', 'One or more priced lines have a 100% discount, so this quote totals $0.00. Warranty, replacement and demo quotes are sometimes saved this way on purpose.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setZeroTotalPending(null); }}>{t('form.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { void confirmZeroTotalSave(); }}>
              {t('form.saveAnyway', 'Save anyway')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MiniSiteFormDialog
        open={miniSiteOpen}
        onOpenChange={setMiniSiteOpen}
        customerId={customerMongoOrgId || ''}
        site={editingSiteData as Parameters<typeof MiniSiteFormDialog>[0]['site']}
        tag={miniSiteTag}
        onSaved={handleSiteSaved}
      />
      <MiniUserFormDialog
        open={miniUserOpen}
        onOpenChange={setMiniUserOpen}
        customerId={customerMongoOrgId || ''}
        onSaved={handleUserSaved}
      />
      <AddExistingContractualManagerDialog
        open={addExistingManagerOpen}
        onOpenChange={setAddExistingManagerOpen}
        organisationId={customerMongoOrgId || ''}
        taggedIds={taggedManagerIds}
        onAdded={handleExistingManagerAdded}
      />
      {canCreateShippingLabel && (
        <CreateShippingLabelModal
          open={shippingLabelOpen}
          onOpenChange={setShippingLabelOpen}
          quoteId={pgQuoteId}
          mongoQuoteId={mongoQuoteId}
        />
      )}
      {canCreateShippingLabel && rmaFromQuoteOpen && (
        <CreateRmaFromQuoteModal
          open={rmaFromQuoteOpen}
          onOpenChange={setRmaFromQuoteOpen}
          quoteId={pgQuoteId}
          source="demo"
        />
      )}
    </Dialog>
  );
}
